import * as THREE from 'three';
import { AdvancedPOICamera } from '../camera/AdvancedPOICamera';
import { AnnotationOverlay } from '../annotations/AnnotationOverlay';
import { FlyCamera } from '../cameras/FlyCamera';
import { CameraManager } from '../cameras/CameraManager';
import { POIData } from '../poi/POIData';
import { Mesh3D } from '../shapes/Mesh3D';
import { SettingsProperty } from '../properties';
import { App3D } from '../core/3DApp';
import { Vector3, Quaternion, LayoutMode, Color } from '../types';
import { Vector2 } from '../types/Vector2';
import { DeviceInfo } from '../utils/DeviceInfo';
import { POI } from '../poi/POI';
import { POISystemState } from '../poi/POISystemState';
import { Logger } from '../core/Logger';
import { InputEventManager } from '../core/InputEventManager';
import { Transform } from '../core/Transform';
import { TransformPropertyMode } from '../core/Transform';
import { InterpolatedProperty } from '../properties/InterpolatedProperty';
import { Easing } from '../types/easing';
import { UIUpdateManager } from '../../ui/UIUpdateManager';
import { UIPerformance } from '../../ui';
import { MarkdownParser } from '../serialization/MarkdownParser';
import { MarkdownExporter } from '../serialization/MarkdownExporter';
import { SceneNode } from '../core/SceneNode';
import { Layout } from '../layout/Layout';
import { LayoutData, LayouterFactory, LayouterType } from '../layout/layouters';
import { loadModernLayouters } from '../layout/modern/index';
import { TestingConfig } from '../core/TestingConfig';
import { SceneNodeTransformTest } from '../test/SceneNodeTransformTest';
import { AssetLoadingBus } from '../core/AssetLoadingBus';

// ==================== FIELD AWARENESS IMPORTS ====================
import { FieldContext } from '../field/FieldContext';
import { FieldParameters } from '../field/FieldParameters';
import { FieldTopology, FieldLayoutType, HoverDirection, AlignmentMode, UpVectorMode, RotationMode } from '../field/FieldTypes';

// ==================== LOADING SYSTEM IMPORTS ====================
import { LoadingManager, LoadingPhase } from '../core/LoadingManager';
import { MaterialLoadingManager } from '../materials/MaterialLoadingManager';

// ==================== MANAGER SYSTEM IMPORTS ====================
import { DebugSystemManager } from '../debug/DebugSystemManager';

import { InputManager } from '../input/InputManager';
import { POIDialogManager } from '../debug/POIDialogManager';
import { showDebugMessage, DebugOverlay } from '../debug/DebugOverlay';

// Add after other property declarations
import { BoundsAnimation } from '../animations/BoundsAnimation';

// ==================== AR SYSTEM IMPORTS ====================
import { ARManager } from '../ar/ARManager';

// ==================== MANAGER IMPORTS ====================
import { DebugManager } from './managers/DebugManager';

// 🧪 EXPERIMENT: Import HTMLLabelMesh for testing
import { HTMLLabelMesh } from '../shapes/HTMLLabelMesh';
import { Scene } from '../core/Scene';
import { TracksFactory } from '../../integrations/tracks/TracksFactory';
import { UTMProjection } from '../../integrations/tracks/UTMProjection';
import { POIConnectionSystem } from '../poi/POIConnectionSystem';

/**
 * A standalone application for the Engine Demo UI.
 * Enhanced with Field Awareness capabilities for organic POI arrangements.
 */
export class EngineDemoApp extends App3D<POISystemState> {
    protected camera: AdvancedPOICamera | null = null;
    // Prevent demo/test markdown auto-loading when the UI will load a presentation
    public suppressDemoLoad: boolean = false;
    
    // ==================== CAMERA SYSTEM ====================
    protected cameraManager: CameraManager = new CameraManager();
    private flyCamera: FlyCamera | null = null;
    private isSpectatorMode: boolean = false;
    
    // We now manage a list of POIs, but the active state is in POISystemState.
    protected pois: POI[] = [];
    private unsubscribeDblClick: (() => void) | null = null;
    /** MeshPoints auf Knowledge-Basis: Marker + Punkt-Hopping der aktiven POI. */
    private annotationOverlay: AnnotationOverlay = new AnnotationOverlay();
    private unsubscribeClick: (() => void) | null = null;
    
    // --- Layout System ---
    protected layout: Layout | null = null;
    private layoutData: LayoutData;

    // --- POI Connection System ---
    private _poiConnectionSystem: POIConnectionSystem | null = null;

    // ==================== FIELD AWARENESS SYSTEM ====================
    private fieldContext: FieldContext | null = null;
    private lastFieldUpdateTime: number = 0;
    
    // ==================== LOADING SYSTEM ====================
    protected loadingManager: LoadingManager;
    protected materialLoadingManager: MaterialLoadingManager;
    private isRenderLoopStarted: boolean = false;
    
    // ==================== MANAGER SYSTEM ====================
    private debugManager!: DebugSystemManager; // Initialized in initializeManagers()
    private poiDialogManager!: POIDialogManager; // Initialized in initializeManagers()

    private inputManager!: InputManager; // Initialized in initializeManagers()
    
    // ==================== AR SYSTEM ====================
    protected arManager!: ARManager; // Initialized in initializeManagers()
    
    // ==================== DEBUG MANAGER ====================
    protected testDebugManager!: DebugManager; // Initialized in initializeManagers()
    
    // Testing utilities
    protected sceneNodeTransformTest!: SceneNodeTransformTest; // Initialized in constructor
    
    // --- Tooltip Hover State ---
    private _hoveredPOI: POI | null = null;
    private _tooltipPOI: POI | null = null;
    private _hoverTimer: number = 0;
    private _lastMousePositionForHover: Vector2 = new Vector2(-1, -1);
    private _isTooltipRaycastDone: boolean = false;
    private static readonly HOVER_TIME_TO_SHOW_TOOLTIP = 1.0; // seconds
    
    // --- Debug Overlay ---
    private _statsOverlay: HTMLElement | null = null;
    
    // --- Camera Orientation Gizmo (like Blender) ---
    private _gizmoCanvas: HTMLCanvasElement | null = null;
    private _gizmoRenderer: THREE.WebGLRenderer | null = null;
    private _gizmoScene: THREE.Scene | null = null;
    private _gizmoCamera: THREE.OrthographicCamera | null = null;
    private _gizmoAxisHelper: THREE.Group | null = null;
    private _gizmoInfoDisplay: HTMLElement | null = null;
    
    // --- Performance Overlay ---
    private _performanceOverlay: HTMLElement | null = null;
    private _performanceOverlayUpdateTime: number = 0;
    
    // --- AR UI Elements ---
    private _arButton: HTMLElement | null = null;
    private _arOverlay: HTMLElement | null = null;
    
    // Test POI tracking
    private testPOIBatchCounter: number = 0;
    private hasTestPOIs: boolean = false;
    
    // Field Direction Animation
    private fieldDirectionAnimation: BoundsAnimation<any> | null = null;
    private isFieldDirectionAnimating: boolean = false;
    // Guard for loading manager task updates
    private _modelLoadingTaskActive: boolean = false;
    // When true, addPOI skips its per-POI applyLayout call. The bulk caller is
    // responsible for calling applyLayoutWithProcessCurveUpdate() once at the end.
    // Avoids O(n²) layout work during multi-POI loads (e.g. parsing a 41-slide pres).
    protected _isBulkLoading: boolean = false;
    // Set true whenever something that would affect layout changes (POI mesh becomes ready,
    // texture aspect changes, etc.). Deferred reapplies check this and skip if nothing dirty.
    private _layoutDirty: boolean = false;
    
    // --- Mouse Drag Tracking for Camera Control ---
    private _mouseDownPosition: Vector2 = new Vector2(-1, -1);
    private _totalMouseDragDistance: number = 0;
    private static readonly DRAG_THRESHOLD_FOR_CLICK_SUPPRESSION = 5; // pixels
    
    constructor(
        canvas: HTMLCanvasElement,
        cssContainer: HTMLElement
    ) {
        super();
        this.systemState = new POISystemState(this.eventManager);
        this.attachUI(canvas, cssContainer);
        
        // Initialize loading system
        this.loadingManager = new LoadingManager(this.eventManager);
        this.materialLoadingManager = MaterialLoadingManager.initialize(this.eventManager);
        
        // Initialize layout system using Singleton pattern
        this.layoutData = new LayoutData(LayouterType.Grid);
        LayoutData.setInstance(this.layoutData);
        
        // ⚠️ Default: Field Awareness disabled (can be enabled via UI)
        this.layoutData.fieldAwarenessEnabled.setImmediate(false);
        
        // Initialize Field Awareness
        this.initializeFieldAwareness();
        
        // Setup fade labels on active functionality
        this.setupfadeLabelOnActive();
        
        // Connect AppSettings layout property to actual layout application
        this.setupLayoutPropertyHandlers();
        
        // Connect Field Awareness property handlers
        this.setupFieldAwarenessPropertyHandlers();
        
        // ==================== INITIALIZE MANAGERS ====================
        this.initializeManagers();
        
        // Initialize testing utilities (will be created later when sceneManager is available)
        
        // POIs now receive layoutData directly in constructor - no need for global access
    }

    // ==================== MANAGER INITIALIZATION ====================
    
    /**
     * Initialize all manager instances
     */
    private initializeManagers(): void {
        // 🚀 Initialize Modern Layouters from TestField
        loadModernLayouters().then(() => {
            //Logger.Instance.Info('✅ ModernLayouters loaded and available globally', 'Layout');
        }).catch((error) => {
            Logger.Instance.Error('❌ Failed to load ModernLayouters: ' + error, 'Layout');
        });

        // Debug System Manager - handles F4 dialog, stats, performance overlay
        this.debugManager = new DebugSystemManager(
            this.eventManager,
            () => this.appSettings,
            () => this.sceneSettings,
            () => this.getLayoutData()
        );
        
        // POI Dialog Manager - handles F7 POI list dialog
        this.poiDialogManager = new POIDialogManager(
            this.eventManager,
            () => this.pois,
            () => {
                // Find the active POI from the pois array
                return this.pois.find(poi => poi.isActive) || null;
            }
        );
        
        // Expose for debugging
        (window as any).poiDialogManager = this.poiDialogManager;
        (window as any).annotationOverlay = this.annotationOverlay;
        // Mess-Handle fuer visuelle Gates: Kamera- und Aktiv-POI-Pose auslesen,
        // um Innen/Aussen-Fragen zu MESSEN statt aus Frames herzuleiten.
        (window as any).presenterDebug = {
            pose: () => {
                const cam: any = (this.camera as any)?._camera;
                const poi: any = (this.camera as any)?._targetPOI;
                const wp = poi?.transform?.getWorldPosition?.();
                const wq = poi?.transform?.worldQuaternion?.value;
                return {
                    cam: cam ? [cam.position.x, cam.position.y, cam.position.z] : null,
                    poi: poi?.name ?? null,
                    poiPos: wp ? [wp.x, wp.y, wp.z] : null,
                    poiQuat: wq ? [wq.x, wq.y, wq.z, wq.w] : null,
                };
            },
        };
        (window as any).testModernLayouters = () => this.testModernLayouters();
        
        // 🧪 EXPERIMENT: HTMLLabelMesh aspect ratio approaches (REMOVED - unified with LabelMesh scaling)
        (window as any).testAspectRatioApproaches = {
            info: () => {
                console.log('ℹ️ Aspect ratio experiments have been removed and unified with LabelMesh scaling system');
                console.log('🎯 HTMLLabelMesh now uses the same scaling approach as other LabelMesh types');
            }
        };

        // 📱 EXPERIMENT: Expose Screen HTML label testing
        (window as any).testScreenLabels = {
            enable: () => {
                const poi = this.getCurrentPOI();
                if (poi) {
                    poi.switchLabelType('screen-html');
                    console.log('📱 Switched to Screen HTML mode - label should appear as 2D overlay');
                } else {
                    console.log('❌ No active POI found');
                }
            },
            disable: () => {
                const poi = this.getCurrentPOI();
                if (poi) {
                    poi.switchLabelType('canvas');
                    console.log('🎨 Switched back to Canvas mode');
                } else {
                    console.log('❌ No active POI found');
                }
            },
            cycle: () => {
                const poi = this.getCurrentPOI();
                if (poi) {
                    const current = poi.poidata.labelRenderMode.value;
                    const next = current === 'canvas' ? 'html' : 
                                current === 'html' ? 'screen-html' : 'canvas';
                    poi.switchLabelType(next);
                    //console.log(`🔄 Cycled: ${current} → ${next}`);
                } else {
                    console.log('❌ No active POI found');
                }
            },
            // debug: () => {
            //     console.log('🔍 === LABEL DEBUG INFO ===');
            //     this.pois.forEach((poi, index) => {
            //         const labelType = poi.poidata.labelRenderMode.value;
            //         const hasLabel = poi._label ? true : false;
            //         const labelClass = poi._label ? poi._label.constructor.name : 'none';
            //         const isVisible = poi._label ? poi._label.visible : false;
            //         //console.log(`POI ${index}: "${poi.name}"`);
            //         //console.log(`  📊 Label Type: ${labelType}`);
            //         //console.log(`  🏷️ Has Label: ${hasLabel} (${labelClass})`);
            //         //console.log(`  👁️ Visible: ${isVisible}`);
            //         //console.log(`  📍 Position: ${poi.transform.position.value.x.toFixed(2)}, ${poi.transform.position.value.y.toFixed(2)}, ${poi.transform.position.value.z.toFixed(2)}`);
            //         console.log('---');
            //     });
                
            //     // Check screen HTML container
            //     const screenContainer = document.getElementById('screen-html-labels-container');
            //     const screenLabels = screenContainer ? screenContainer.children.length : 0;
            //     //console.log(`📱 Screen HTML Container: ${screenContainer ? 'exists' : 'missing'}`);
            //     //console.log(`📱 Screen HTML Labels in DOM: ${screenLabels}`);
                
            //     // Check CSS3D scene for HTML labels
            //     const css3dScene = Scene.Instance?.getCSS3DScene();
            //     const css3dObjects = css3dScene ? css3dScene.children.length : 0;
            //     //console.log(`🌐 CSS3D Scene Objects: ${css3dObjects}`);
                
            //     // List all DOM elements that might be labels
            //     const allLabelElements = document.querySelectorAll('[class*="label"], [id*="label"], .screen-html-label');
            //     //console.log(`🏷️ All label-like DOM elements: ${allLabelElements.length}`);
            //     allLabelElements.forEach((el, i) => {
            //         //console.log(`  ${i}: ${el.tagName}.${el.className} - "${el.textContent?.substring(0, 50)}..."`);
            //     });
            // }
        };
        
        // Input Manager - handles keyboard and mouse input
        this.inputManager = new InputManager(this.eventManager);
        
        // Setup input manager event handlers
        this.setupInputManagerEventHandlers();
        
        // AR Manager - handles WebXR AR functionality
        this.arManager = new ARManager(this.eventManager);
        
        // Setup AR event listeners
        this.setupAREventHandlers();
        
        // Test Debug Manager - handles debug methods like showTorus, debugMaterialLoading, etc.
        this.testDebugManager = new DebugManager(
            this.systemState,
            this.sceneSettings,
            this.appSettings,
            this.materialLoadingManager,
            (poi: POI) => this.addPOI(poi),
            () => this.clearAllPOIs(),
            (poi: POI) => this.setActivePOI(poi),
            () => this.pois,
            () => this.camera,
            () => this.scene,
            () => this._gizmoCanvas,
            () => this._gizmoRenderer,
            () => this._gizmoScene,
            () => this._gizmoCamera,
            () => this._gizmoAxisHelper
        );
        
        // Camera switching (Ctrl+K / Cmd+K) is handled directly in InputManager
        
        //Logger.Instance.Info('🏗️ Debug Manager initialized', 'Managers');
        //Logger.Instance.Info('🗺️ POI Dialog Manager initialized', 'Managers');
        //Logger.Instance.Info('⌨️ Input Manager initialized', 'Managers');
        //Logger.Instance.Info('🥽 AR Manager initialized', 'Managers');
        //Logger.Instance.Info('🔧 Test Debug Manager initialized', 'Managers');
    }

    // ==================== MANAGER DELEGATION METHODS ====================
    
    // Debug methods now accessed directly via this.testDebugManager

    /**
     * Setup input manager event handlers
     */
    private setupInputManagerEventHandlers(): void {
        // Listen for input actions from the manager
        this.eventManager.on('input:action', (event: any) => {
            this.handleInputAction(event);
        });
        
        this.eventManager.on('input:click', (event: any) => {
            this.handleInputClick(event);
        });
        
        this.eventManager.on('input:doubleclick', (event: any) => {
            this.handleInputDoubleClick(event);
        });
        
        this.eventManager.on('input:mousemove', (event: any) => {
            this.handleInputMouseMove(event);
        });

        // Pinch gestures are normalized by InputManager into the same delta convention as
        // wheel input. Mouse wheels keep using the canvas-scoped listener below; only touch
        // events enter here so a physical wheel is never applied twice.
        this.eventManager.on('input:wheel', (event: any) => {
            const originalType = String(event?.originalEvent?.type || '');
            if (originalType.startsWith('touch')) {
                this.applyWheelZoom(Number(event?.deltaY) || 0);
            }
        });
    }
    
    /**
     * Toggle debug dialog (delegated to DebugSystemManager)
     */
    public toggleDebugDialog(): void {
        this.debugManager.toggleDebugDialog();
    }
    
    // ==================== INPUT HANDLER METHODS ====================
    
    /**
     * Handle input actions from InputManager
     */
    private handleInputAction(event: any): void {
        const { action, modifiers } = event;
        console.log('🔍 EngineDemoApp: handleInputAction called with action:', action);
        
        switch (action) {
            case 'navigate-next':
                this.nextPOI();
                break;
            case 'set-layouter-line':
                try {
                    (this as any).setLayouter('line');
                } catch {}
                break;
            case 'navigate-previous':
                this.previousPOI();
                break;
            case 'toggle-debug':
                console.log('🔍 EngineDemoApp: toggle-debug action triggered, calling debugManager.toggleDebugDialog()');
                this.toggleDebugDialog();
                break;
            case 'show-poi-list':
                console.log('🔍 EngineDemoApp: show-poi-list action triggered, calling poiDialogManager.togglePOIDialog()');
                this.poiDialogManager.togglePOIDialog();
                break;
            case 'toggle-stats':
                this.toggleHtmlCanvasRendering();
                break;
            case 'toggle-performance':
                this.togglePerformanceProfile();
                break;
            case 'toggle-field-awareness':
                this.toggleFieldAwareness();
                break;
            case 'create-test-pois':
                this.createTestPOIs();
                break;
            case 'create-massive-test':
                this.createMassivePOITest();
                break;
            case 'clear-pois':
                this.clearAllPOIs();
                break;
            case 'set-shape-cube':
                this.testDebugManager.showCube();
                break;
            case 'set-shape-sphere':
                this.testDebugManager.showSphere();
                break;
            case 'set-shape-plane':
                this.testDebugManager.showPlane();
                break;
            case 'set-shape-triangle':
                this.testDebugManager.showTriangle();
                break;
            case 'set-shape-hexagon':
                this.testDebugManager.showHexagon();
                break;
            case 'set-shape-torus':
                this.testDebugManager.showTorus();
                break;
            case 'toggle-field-direction-animation':
                this.toggleFieldDirectionAnimation();
                break;
            case 'toggle-ar':
                this.toggleAR();
                break;
            case 'apple-quicklook-ar':
                this.triggerAppleQuickLookAR();
                break;
            case 'toggle-spectator-camera':
                if (modifiers.ctrl || modifiers.cmd) {
                    this.toggleSpectatorMode();
                }
                break;
            default:
                //Logger.Instance.Debug(`🎹 Unhandled input action: ${action}`, 'InputManager');
        }
    }
    
    /**
     * Handle click events from InputManager
     */
    private handleInputClick(event: any): void {
        const { x, y, button, modifiers, originalEvent } = event;
        
        // Ignore clicks that did not originate on the canvas element (e.g., UI overlays)
        if (this.canvas && originalEvent && originalEvent.target) {
            const target = originalEvent.target as HTMLElement;
            if (target !== this.canvas) {
                return;
            }
        }
        
        // Only react to clicks inside the canvas area
        if (this.canvas) {
            const rect = this.canvas.getBoundingClientRect();
            if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                return;
            }
        }

        // Handle POI selection on click
        if (button === 0) { // Left click
            // CHECK: Was there significant mouse dragging during this click?
            // If yes, this was likely a camera orbit operation, not an intentional click
            if (this._totalMouseDragDistance > EngineDemoApp.DRAG_THRESHOLD_FOR_CLICK_SUPPRESSION) {
                //Logger.Instance.Debug(`🖱️ Click suppressed: drag distance ${this._totalMouseDragDistance.toFixed(1)}px > ${EngineDemoApp.DRAG_THRESHOLD_FOR_CLICK_SUPPRESSION}px threshold`, 'InputManager');
                
                // Reset drag distance after suppression
                this._totalMouseDragDistance = 0;
                return;
            }
            
            // Reset drag distance for next click
            this._totalMouseDragDistance = 0;

            // Marker-Tap: sitzt unter dem Klick ein Annotationsmarker der aktiven POI,
            // direkt dorthin springen — vor dem POI-Raycast, sonst frisst die Shape den Klick.
            if (this.canvas && this.scene && this.annotationOverlay.count > 0) {
                const rect = this.canvas.getBoundingClientRect();
                const nx = ((x - rect.left) / rect.width) * 2 - 1;
                const ny = -((y - rect.top) / rect.height) * 2 + 1;
                const hit = this.annotationOverlay.hitTest(nx, ny, this.scene.getCamera());
                if (hit >= 0 && this.annotationOverlay.focusIndex(hit)) {
                    this.applyAnnotationFocus();
                    return;
                }
            }
            
            const poi = this.getPOIFromScreenPoint(x, y);
            if (poi) {
                // Avoid re-selecting the same POI on background/overlay clicks
                const active = this.systemState.activePOI as POI | null;
                if (active && (active === poi || active.name === poi.name)) {
                    return;
                }
                this.setActivePOI(poi);
                //Logger.Instance.Debug(`🖱️ POI selected via click: ${poi.id}`, 'InputManager');
            } else {
                // Explicitly log that no POI was hit and no action should be taken
                //Logger.Instance.Debug(`🖱️ Click on empty space - no POI selection`, 'InputManager');
                // Do NOT call setActivePOI or any other POI-related methods here
            }
        }
    }
    
    /**
     * Handle double-click events from InputManager
     */
    private handleInputDoubleClick(event: any): void {
        const { x, y, button, modifiers, originalEvent } = event;

        // Ignore double-clicks that did not originate on the canvas element (e.g., UI overlays)
        if (this.canvas && originalEvent && originalEvent.target) {
            const target = originalEvent.target as HTMLElement;
            if (target !== this.canvas) {
                return;
            }
        }

        // Only react to double-clicks inside the canvas area
        if (this.canvas) {
            const rect = this.canvas.getBoundingClientRect();
            if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                return;
            }
        }
        
        // Handle POI focus on double-click
        if (button === 0) { // Left double-click
            const poi = this.getPOIFromScreenPoint(x, y);
            if (poi && this.camera) {
                this.camera.setTarget(poi);
                //Logger.Instance.Debug(`🖱️ POI focused via double-click: ${poi.id}`, 'InputManager');
                
                // Also emit UI event for properties dialog (previously handled by InputEventManager)
                this.eventManager.emit('ui:show-properties-for-poi', poi);
                //Logger.Instance.Info(`Double-clicked on POI: ${poi.name}. Firing UI event.`);
            } else {
                // Explicitly log that no POI was hit and no action should be taken
                //Logger.Instance.Debug(`🖱️ Double-click on empty space - no POI focus`, 'InputManager');
                // Do NOT call camera.setTarget or any other POI-related methods here
            }
        }
    }
    
    /**
     * Handle mouse move events from InputManager
     */
    private handleInputMouseMove(event: any): void {
        // Handle hover state updates
        // This could be throttled to avoid performance issues
        // For now, we'll keep it simple
    }
    


    // ==================== FIELD AWARENESS INITIALIZATION ====================
    
    private initializeFieldAwareness(): void {
        // Create basic field context first (UI settings will be synced later)
        const defaultFieldContext = new FieldContext(
            FieldTopology.TwoD,
            FieldLayoutType.Grid,
            new FieldParameters() // Use defaults for now, will be synced with UI later
        );
        
        // Set as global field context for POI access
        this.fieldContext = defaultFieldContext;
        FieldContext.setGlobalContext(defaultFieldContext);
        
        // Setup field event listeners (simplified)
        this.setupFieldEventListeners();
        
        //Logger.Instance.Info('🌪️ Field Awareness System initialized with basic context (will sync with UI later)', 'Field');
    }
    
    private setupFieldEventListeners(): void {
        // Field events can be handled via the eventManager directly
        // No need for POI management events since POIs self-manage
        //Logger.Instance.Debug('🌪️ Field event listeners setup complete', 'Field');
    }
    
    // Field update loop removed - POIs update themselves in the scene graph update loop

    // ==================== ENHANCED STARTUP ====================

    protected override onStart(): void {
        // Don't call super.onStart() immediately - we need to wait for loading first
        // super.onStart();
        
        // Start the initialization process asynchronously
        this.startWithProgressiveLoading();
    }
    
    /**
     * Start the app with progressive loading
     */
    private async startWithProgressiveLoading(): Promise<void> {
        //Logger.Instance.Info("🚀 Starting EngineDemoApp with progressive loading", "Engine");
        
        // Activate UI Performance Mode for better framerate
        UIPerformance.enablePerformanceMode();
        //Logger.Instance.Info("🚀 UI Performance Mode activated - expensive animations disabled", "Performance");
        
        // Wait for loading to complete
        await this.loadingManager.waitUntilReady();
        
        // Now start the actual app
        //Logger.Instance.Info("🎮 EngineDemoApp ready to start - initializing controls", "Engine");
        
        // Call the base start method to set running state and emit events
        super.onStart();
        
        // Log all controls
        //Logger.Instance.Info("🎮 Controls:", "Controls");
        //Logger.Instance.Info("  ← → : Navigate between POIs", "Controls");
        //Logger.Instance.Info("  P   : Toggle performance profiling", "Controls");
        //Logger.Instance.Info("  H   : Toggle HTML canvas rendering", "Controls");
        //Logger.Instance.Info("  F4  : Debug Properties Dialog", "Controls");
        //Logger.Instance.Info("  F7  : POI List Dialog", "Controls");
        //Logger.Instance.Info("  F9  : Presentation Loader Dialog", "Controls");
        //Logger.Instance.Info("  🥽 AR CONTROLS:", "Controls");
                    //Logger.Instance.Info("  V   : Toggle AR Mode (WebXR)", "Controls");
            //Logger.Instance.Info("  Q   : Apple QuickLook AR (iOS Safari)", "Controls");
        //Logger.Instance.Info("  🧭 GIZMO CONTROLS:", "Controls");
        //Logger.Instance.Info("  Shift+O: Toggle Camera Orientation Gizmo", "Controls");
        //Logger.Instance.Info("  🎮 CAMERA CONTROLS:", "Controls");
        //Logger.Instance.Info("  Ctrl+K: Toggle Spectator Camera (WASD + mouse to fly)", "Controls");
        //Logger.Instance.Info("  🌪️ FIELD AWARENESS:", "Controls");
        //Logger.Instance.Info("  F   : Toggle Field Awareness", "Controls");
        //Logger.Instance.Info("  Q   : Breathing Spiral preset", "Controls");
        //Logger.Instance.Info("  W   : Ripple Grid preset", "Controls");
        //Logger.Instance.Info("  E   : Snake Line preset", "Controls");
        //Logger.Instance.Info("  R   : Organic Circle preset", "Controls");
        //Logger.Instance.Info("  🎯 FIELD LAYOUTS:", "Controls");
        //Logger.Instance.Info("  Shift+L : Line Field", "Controls");
        //Logger.Instance.Info("  Shift+G : Grid Field", "Controls");
        //Logger.Instance.Info("  Shift+C : Circle Field", "Controls");
        //Logger.Instance.Info("  Shift+S : Spiral Field", "Controls");
        //Logger.Instance.Info("  Shift+H : Hex Field", "Controls");
        //Logger.Instance.Info("  🔧 DEBUG CONTROLS:", "Controls");
        //Logger.Instance.Info("  Shift+M : Material Loading Debug", "Controls");
        //Logger.Instance.Info("  Ctrl+Shift+F : Force Complete Loading", "Controls");

        if (!this.scene) return;

        // Pre-compile all shaders and upload textures to GPU before first frame
        // This prevents frame stutters when new objects become visible during navigation
        const renderer = this.scene.getRenderer();
        const camera = this.scene.getCamera();
        if (renderer && camera) {
            renderer.compile(this.scene.getThreeScene(), camera);
        }

        this.setupKeyHandlers();
        this.setupMouseHandlers();
        //this.createStatsOverlay();

        // Now start the render loop
        this.startRenderLoop();
        
        //Logger.Instance.Info("🎉 EngineDemoApp fully started - render loop active", "Engine");

        // To run the transform tests, uncomment the line below
        // this.runTransformTests();
    }
    
    /**
     * Start the render loop manually (called after loading is complete)
     */
    private startRenderLoop(): void {
        if (this.isRenderLoopStarted) {
            Logger.Instance.Warn("Render loop already started", "Engine");
            return;
        }
        
        this.isRenderLoopStarted = true;
        //Logger.Instance.Info("🔄 Starting render loop", "Engine");
        
        // This is essentially the same as App3D's startUpdateLoop but controlled by us
        let lastUpdateTime = performance.now();
        const update = (currentTime: number) => {
            if (this.systemState.status === 'stopped') return;
            
            requestAnimationFrame(update);

            const deltaTime = currentTime - lastUpdateTime;
            lastUpdateTime = currentTime;
            
            if (this.systemState.isRunning()) {
                // Update scene
                this.sceneManager?.update(deltaTime / 1000);
                
                // Update and render the main scene
                this.scene?.updateAndRender(deltaTime / 1000);

                // Call our update method
                this.onUpdate(deltaTime / 1000);
                
                // Emit update event
                this.eventManager.emit('system:update', { 
                    deltaTime: deltaTime / 1000
                });
            }
        };
        requestAnimationFrame(update);
    }

    // Initialization logic is now in onInitialize, as required by App3D.
    protected async onInitialize(): Promise<void> {
        //Logger.Instance.Info("🚀 EngineDemoApp onInitialize() started with progressive loading", "Initialization");
        
        if (!this.scene) {
            this.addError("Scene not initialized");
            return;
        }

        // ==================== PHASE 1: SCENE SETUP ====================
        this.loadingManager.startPhase(LoadingPhase.SceneSetup, 'Setting up scene');

        // Apply blur/input-block to canvas while loading
        try {
            const canvasEl = (this.canvas || document.querySelector('canvas')) as HTMLCanvasElement | null;
            this.loadingManager.attachCanvas(canvasEl);
        } catch {}
        this.loadingManager.addTask('input-setup', 'Input Event Manager', 1);
        this.loadingManager.addTask('ui-manager', 'UI Manager', 1);
        
        // 1. Setup InputEventManager with canvas for mouse wheel events
        if (this.canvas) {
            InputEventManager.Instance.setCanvas(this.canvas);
            InputEventManager.Instance.setScene(this.scene.getThreeScene());
            InputEventManager.Instance.setCamera(this.scene.getThreeScene().children.find(obj => obj instanceof THREE.Camera) as THREE.Camera);
            //Logger.Instance.Info("🎮 InputEventManager configured with canvas for mouse wheel zoom", "Input");
            
            // BACKUP: Add direct wheel event listener to test if canvas receives events
            this.canvas.addEventListener('wheel', (event) => {
                event.preventDefault(); // Prevent page scroll
                
                this.applyWheelZoom(event.deltaY);
            }, { passive: false });
            
            // Make canvas focusable and focus it to ensure it can receive events
            this.canvas.tabIndex = 0;
            this.canvas.style.outline = 'none'; // Remove focus outline
            this.canvas.addEventListener('click', () => {
                this.canvas?.focus();
            });
            
            // Focus the canvas immediately
            this.canvas.focus();
        }
        this.loadingManager.completeTask('input-setup');
        
        // Add the UI Manager to the scene
        //Logger.Instance.Info("🎮 Adding UI Manager...", "UI");
        this.sceneManager?.addSceneNode(new UIUpdateManager());
        this.loadingManager.completeTask('ui-manager');
        this.loadingManager.completePhase();
        
        // ==================== PHASE 2: CAMERA SETUP ====================
        this.loadingManager.startPhase(LoadingPhase.CameraSetup, 'Initializing camera');
        this.loadingManager.addTask('camera-creation', 'Advanced POI Camera', 1);
        this.loadingManager.addTask('ar-setup', 'AR System Setup', 1);
        
        // 2. Create a camera and set it on the scene
        //Logger.Instance.Info("🎥 Creating camera...", "Camera");
        this.camera = new AdvancedPOICamera(this.scene.getThreeScene(), this.appSettings, this.systemState);
        this.scene.setCamera(this.camera.internalCamera);
        
        // 3. Update InputEventManager with the actual camera
        InputEventManager.Instance.setCamera(this.camera.internalCamera);
        this.loadingManager.completeTask('camera-creation');
        
        // 4. Initialize AR Manager with scene, renderer, and camera
        //Logger.Instance.Info("🥽 Initializing AR Manager...", "AR");
        // Initialize FlyCamera for spectator mode
        this.flyCamera = new FlyCamera('SpectatorCamera', this.appSettings);
        this.cameraManager.addCamera(this.flyCamera);
        
        // Set the Three.js camera for the fly camera (same as POI camera)
        if (this.camera?.internalCamera) {
            this.flyCamera.setRenderCamera(this.camera.internalCamera);
        }
        
        this.arManager.initialize(
            this.scene.getRenderer(), 
            this.scene.getThreeScene(), 
            this.camera.internalCamera,
            this.camera  // Pass the AdvancedPOICamera for proper management
        );
        this.loadingManager.completeTask('ar-setup');
        this.loadingManager.completePhase();
        
        // ==================== FAST PATH: EMPTY INIT ====================
        // When the UI drives the content load (suppressDemoLoad, e.g. ?post= / ?pres= URLs),
        // phases 3-6 have nothing to do: no POIs, no materials, no models, no layout.
        // Running them anyway costs ~600ms of pure timer waits (200ms asset fallback +
        // 50ms debounce + 300ms "finalizing" sleep) and logs bogus "applyLayout skipped"
        // warnings. The real content pipeline is loadPresentationFromMarkdown().
        if (this.suppressDemoLoad) {
            this.setupLayoutSystem();
            this.loadingManager.complete();
            this.createARButton();
            if (this.sceneManager) {
                this.sceneNodeTransformTest = new SceneNodeTransformTest(this.sceneManager);
            }
            Logger.Instance.Info('Engine ready (fast path — content load deferred to UI)', 'Init');
            return;
        }

        // ==================== PHASE 3: POI LOADING ====================
        this.loadingManager.startPhase(LoadingPhase.POILoading, 'Parsing presentation');
        this.loadingManager.addTask('poi-parsing', 'Parse markdown file', 1);
        this.loadingManager.addTask('poi-creation', 'Create POI objects', 1);

        // 4. Load POIs from Markdown (demo default)
        await this.loadPoisFromMarkdown();
        this.loadingManager.completeTask('poi-parsing');
        this.loadingManager.completeTask('poi-creation');
        this.loadingManager.completePhase();
        
        // ==================== PHASE 4: MATERIAL LOADING ====================
        this.loadingManager.startPhase(LoadingPhase.MaterialLoading, 'Loading media');
        this.loadingManager.addTask('material-registration', 'Register materials for loading', 1);
        this.loadingManager.addTask('material-loading', 'Load all materials', 3);
        
        // Register all materials for loading
        await this.registerAllMaterialsForLoading();
        this.loadingManager.completeTask('material-registration');
        
        // 🎯 FIX: Explicitly trigger texture loading AFTER registration
        this.triggerTextureLoadingForAllPOIs();
        
        // Wait for all materials to load
        await this.waitForAllMaterialsToLoad();
        this.loadingManager.completeTask('material-loading');
        this.loadingManager.completePhase();
        
        // ==================== PHASE 5: MODEL LOADING & LAYOUT APPLICATION ====================
        this.loadingManager.startPhase(LoadingPhase.LayoutApplication, 'Building layout');
        this.loadingManager.addTask('field-setup', 'Field Awareness setup', 1);
        this.loadingManager.addTask('model-loading', '3D model loading', 5);
        this.loadingManager.addTask('layout-setup', 'Layout system setup', 1);
        this._modelLoadingTaskActive = true;
        
        // POIs self-manage field awareness - no initialization needed
        this.loadingManager.completeTask('field-setup');
        
        // 5a. Track GLTF/USDZ loading progress
        let pendingAssets = 0;
        const onStart = () => { if (!this._modelLoadingTaskActive) return; pendingAssets++; this.loadingManager.updateTask('model-loading', Math.min(90, pendingAssets * 5)); };
        const onProgress = (p: any) => { if (!this._modelLoadingTaskActive) return; this.loadingManager.updateTask('model-loading', Math.min(95, (p?.progress ?? 0) * 0.9)); };
        const onComplete = () => { if (!this._modelLoadingTaskActive) return; pendingAssets = Math.max(0, pendingAssets - 1); if (pendingAssets === 0) this.loadingManager.updateTask('model-loading', 100); };
        const onError = () => { if (!this._modelLoadingTaskActive) return; pendingAssets = Math.max(0, pendingAssets - 1); this.loadingManager.updateTask('model-loading', 100); };
        AssetLoadingBus.on('asset:load-start', onStart);
        AssetLoadingBus.on('asset:load-progress', onProgress);
        AssetLoadingBus.on('asset:load-complete', onComplete);
        AssetLoadingBus.on('asset:load-error', onError);

        // 5b. Setup layout system (will be applied after models load)
        this.setupLayoutSystem();
        // Ensure layouter reflects parsed global header immediately
        try {
            const layouterStr = this.layoutData.layouter;
            if (layouterStr && typeof layouterStr === 'string') {
                this.setLayouter(layouterStr as any);
            }
        } catch {}

        // 5c. Defer first layout application until heavy assets complete.
        // (FPS stabilization gate removed — it added 3s of pure dead-time when poiCount=0,
        // because POIs are only loaded later via the URL param.)
        const applyInitialLayout = async () => {
            // Small debounce to batch multiple completes
            await new Promise(r => setTimeout(r, 50));
            if (pendingAssets === 0) {
                this.applyLayoutWithProcessCurveUpdate();

                // Unsubscribe after initial layout + FPS stabilization
                AssetLoadingBus.off('asset:load-complete', applyInitialLayout);
                AssetLoadingBus.off('asset:load-error', applyInitialLayout);
                this.loadingManager.completeTask('model-loading');
                this.loadingManager.completeTask('layout-setup');
                this.loadingManager.completePhase();
                this._modelLoadingTaskActive = false;

                // ==================== PHASE 6: READY ====================
                this.loadingManager.startPhase(LoadingPhase.Ready, 'Finalizing');
                this.loadingManager.addTask('final-setup', 'Final preparations', 1);
                await new Promise(resolve => setTimeout(resolve, 300));
                this.loadingManager.completeTask('final-setup');
                this.loadingManager.complete();
            }
        };
        AssetLoadingBus.on('asset:load-complete', applyInitialLayout);
        AssetLoadingBus.on('asset:load-error', applyInitialLayout);

        // Fallback: if no assets started within a short window, proceed to layout+ready gate
        setTimeout(() => { if (pendingAssets === 0) { void applyInitialLayout(); } }, 200);

        // Wait for overlay to report ready (complete()) before continuing
        await this.loadingManager.waitUntilReady();
        
        // 🎯 PROFESSIONAL UI: Don't create gizmo by default - only via shortcut (Shift+O)
        // this.createGizmo();
        
        // Create AR button UI
        this.createARButton();
        
        // Initialize testing utilities now that sceneManager is available
        if (this.sceneManager) {
            this.sceneNodeTransformTest = new SceneNodeTransformTest(this.sceneManager);
        }
        
        //Logger.Instance.Info("✅ EngineDemoApp initialized with progressive loading complete", "Initialization");
    }
    
    /**
     * Register all materials for loading tracking
     */
    protected async registerAllMaterialsForLoading(): Promise<void> {
        const registrationStart = performance.now();
        ////Logger.Instance.Info(`📋 [TIMING] Starting material registration at ${registrationStart.toFixed(2)}ms`, 'MaterialLoading');
        
        // Reset material loading manager
        const resetStart = performance.now();
        this.materialLoadingManager.reset();
        const resetEnd = performance.now();
        ////Logger.Instance.Debug(`🔄 [TIMING] Material loading manager reset in ${(resetEnd - resetStart).toFixed(2)}ms`, 'MaterialLoading');
        
        // Register materials for each POI
        let textureCount = 0;
        let videoCount = 0;
        
        for (const poi of this.pois) {
            const poiStart = performance.now();
            const uris = poi.poidata.uri.value || []; // Now an array
            
            // Process all URIs in the array (typically front + optional back texture)
            for (const singleUri of uris) {
                if (singleUri && singleUri.trim() !== '') {
                    const isVideo = ['.mp4', '.webm', '.ogv'].some(ext => singleUri.toLowerCase().endsWith(ext));
                    const isGLTF = ['.glb', '.gltf'].some(ext => singleUri.toLowerCase().endsWith(ext));
                    const isUSDZ = singleUri.toLowerCase().endsWith('.usdz');
                    const isTexture = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'].some(ext => singleUri.toLowerCase().endsWith(ext));
                    
                    if (isVideo) {
                    videoCount++;
                    const videoStart = performance.now();
                    // For videos, we need to create the video element first
                    const videoElement = document.createElement('video');
                    videoElement.src = singleUri;
                    videoElement.preload = 'auto';
                    videoElement.crossOrigin = 'anonymous';
                    this.materialLoadingManager.registerVideo(singleUri, videoElement);
                    const videoEnd = performance.now();
                    ////Logger.Instance.Debug(`🎬 [TIMING] Video registered: ${uri} in ${(videoEnd - videoStart).toFixed(2)}ms`, 'MaterialLoading');
                } else if (isTexture) {
                    textureCount++;
                    const textureStart = performance.now();
                    // For textures, register with THREE.js loading manager
                    this.materialLoadingManager.registerTexture(singleUri);
                    const textureEnd = performance.now();
                    ////Logger.Instance.Debug(`🖼️ [TIMING] Texture registered: ${uri} in ${(textureEnd - textureStart).toFixed(2)}ms`, 'MaterialLoading');
                } else if (isGLTF) {
                    // GLB/GLTF files are handled by GLTFShape directly, not by MaterialLoadingManager
                    ////Logger.Instance.Debug(`🏗️ [TIMING] Skipping GLB/GLTF file for MaterialLoadingManager: ${uri}`, 'MaterialLoading');
                } else if (isUSDZ) {
                    // USDZ files are handled by USDZShape for Apple QuickLook AR, not by MaterialLoadingManager
                    ////Logger.Instance.Debug(`🍎 [TIMING] Skipping USDZ file for MaterialLoadingManager: ${uri}`, 'MaterialLoading');
                    } else {
                        // Unknown file type, log warning but don't try to load as texture
                        Logger.Instance.Warn(`⚠️ Unknown file type for URI: ${singleUri} - skipping material loading`, 'MaterialLoading');
                    }
                } // End of uri.trim() check
            } // End of URI loop
            
            const poiEnd = performance.now();
            ////Logger.Instance.Debug(`📍 [TIMING] POI '${poi.name}' processed in ${(poiEnd - poiStart).toFixed(2)}ms`, 'MaterialLoading');
        }
        
        const finalizeStart = performance.now();
        const progress = this.materialLoadingManager.getProgress();
        ////Logger.Instance.Info(`📊 [TIMING] Registered ${progress.totalMaterials} materials (${textureCount} textures, ${videoCount} videos)`, 'MaterialLoading');
        
        // Finalize registration - this will trigger immediate completion if no materials were registered
        this.materialLoadingManager.finalizeRegistration();
        const finalizeEnd = performance.now();
        
        const registrationEnd = performance.now();
        const totalTime = registrationEnd - registrationStart;
        ////Logger.Instance.Info(`✅ [TIMING] Material registration completed in ${totalTime.toFixed(2)}ms (finalize: ${(finalizeEnd - finalizeStart).toFixed(2)}ms)`, 'MaterialLoading');
    }
    
    /**
     * Wait for all materials to load
     */
    protected async waitForAllMaterialsToLoad(): Promise<void> {
        //Logger.Instance.Info('⏳ Waiting for all materials to load...', 'MaterialLoading');
        
        // Setup progress tracking
        const updateProgress = () => {
            const progress = this.materialLoadingManager.getProgress();
            this.loadingManager.updateTask('material-loading', progress.progress, 
                `${progress.loadedMaterials}/${progress.totalMaterials} materials loaded`);
        };
        
        // Listen for material loading progress
        this.eventManager.on('material-loading:progress', updateProgress);
        
        // Setup timeout for hanging loading
        const loadingTimeout = setTimeout(() => {
            Logger.Instance.Warn('⏰ Material loading taking too long - showing debug info', 'MaterialLoading');
            this.testDebugManager.debugMaterialLoading();
            
            // Force complete after 30 seconds
            setTimeout(() => {
                Logger.Instance.Warn('🚫 Force completing material loading due to timeout', 'MaterialLoading');
                this.materialLoadingManager.forceComplete();
            }, 10000); // Additional 10 seconds before force complete
            
        }, 20000); // Show debug after 20 seconds
        
        try {
            // Wait for all materials to load
            await this.materialLoadingManager.waitUntilAllLoaded();
            
            clearTimeout(loadingTimeout);
            
            const finalProgress = this.materialLoadingManager.getProgress();
            //Logger.Instance.Info(`✅ All materials loaded! Success: ${finalProgress.loadedMaterials}, Failed: ${finalProgress.failedMaterials}`, 'MaterialLoading');
            
            // Show debug info if there were failures
            if (finalProgress.failedMaterials > 0) {
                this.testDebugManager.debugMaterialLoading();
            }
            
        } catch (error) {
            clearTimeout(loadingTimeout);
            Logger.Instance.Error(`❌ Error waiting for materials: ${error}`, 'MaterialLoading');
            this.testDebugManager.debugMaterialLoading();
        } finally {
            // Clean up event listener
            this.eventManager.off('material-loading:progress', updateProgress);
        }
    }
    
    /**
     * Debug material loading issues
     */
    // MOVED TO DebugManager.ts: lines 829-860
    protected onUpdate(deltaTime: number): void {
        if (this.annotationOverlay.count > 1 && this.scene) {
            this.annotationOverlay.declutter(this.scene.getCamera(), window.innerWidth, window.innerHeight);
        }
        let updateStart = 0;
        if (TestingConfig.performance) {
            updateStart = performance.now();
            this._performanceStats.frameCount++;
        }

        // Handle zoom BEFORE InputEventManager.update() resets mouseWheelDelta
        // Zoom läuft ausschließlich über den wheel-Listener (applyWheelZoom).
        // Früher rechneten hier ein zweiter und im InputManager ein dritter Pfad mit
        // teils umgekehrtem Vorzeichen gegen ihn an — siehe applyWheelZoom().
        // this.handleZoomInput();

        // Update the global input manager (resets per-frame deltas)
        InputEventManager.Instance.update();

        // Track mouse drag distance for click suppression
        this.updateMouseDragTracking();

        // Handle orbit via mouse drag
        this.handleOrbitInput();

        // Update camera system (but not during AR sessions)
        if (!this.arManager?.getStatus().isActive) {
            if (this.isSpectatorMode && this.flyCamera) {
                // Update spectator camera
                this.flyCamera.update(deltaTime);
            } else {
                // Update POI camera
                this.camera?.update(deltaTime);
            }
        }
        
        // Update AR Manager
        this.arManager?.update(deltaTime);

        // DOF auto-focus: every frame compute focus distance = camera→activePOI.
        // Use the *underlying THREE.Camera position* (not POICamera wrapper transform) because
        // BokehPass evaluates depth in three.js camera-space.
        if (this.sceneSettings?.dofEnabled?.value && this.scene) {
            try {
                const poi = this.systemState?.activePOI as any;
                if (poi) {
                    const threeCam = (this.scene as any).camera as { position: { x: number; y: number; z: number } } | undefined;
                    const poiTransform = poi.transform;
                    const poiPos = poiTransform?.getWorldPosition?.();
                    if (threeCam && poiPos) {
                        const dx = threeCam.position.x - poiPos.x;
                        const dy = threeCam.position.y - poiPos.y;
                        const dz = threeCam.position.z - poiPos.z;
                        const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
                        if (dist > 0.01 && isFinite(dist)) {
                            this.scene.setDOFFocusDistance(dist);
                            // Throttled log (once per second roughly)
                            const now = performance.now();
                            if (!(this as any)._lastDofLog || now - (this as any)._lastDofLog > 1000) {
                                (this as any)._lastDofLog = now;
                                Logger.Instance.Info(`DOF focus=${dist.toFixed(2)} cam=(${threeCam.position.x.toFixed(1)},${threeCam.position.y.toFixed(1)},${threeCam.position.z.toFixed(1)}) poi=(${poiPos.x.toFixed(1)},${poiPos.y.toFixed(1)},${poiPos.z.toFixed(1)}) ${poi.name}`, 'DOF');
                            }
                        }
                    }
                }
            } catch (e) {
                Logger.Instance.Warn(`DOF auto-focus error: ${e}`, 'DOF');
            }
        }
        
        // Update field direction animation if active
        if (this.fieldDirectionAnimation && this.isFieldDirectionAnimating) {
            this.fieldDirectionAnimation.update(deltaTime);
        }
        
        // Update process curve animation
        if (this.layout) {
            this.layout.updateProcessCurveAnimation(deltaTime);
        }
        
        // Tooltip disabled for performance - prevents raycast operations
        // this._updateHoverState(deltaTime);

        // Disabled stats overlay for performance - causes frequent DOM updates
        this.updateStatsOverlay();
        
        // Update camera orientation gizmo
        this.updateGizmo();

        if (TestingConfig.performance) {
            this._performanceStats.updateTime += performance.now() - updateStart;
            
            // Update performance overlay every second
            this.updatePerformanceOverlay();
            
            // Log summary every 5 seconds
            if (this._performanceStats.frameCount % 300 === 0) { // Assuming ~60fps
                this._logPerformanceReport();
                this._resetPerformanceStats();
            }
        }
    }

    // ==================== FIELD AWARENESS API ====================
    
    /**
     * Enable Field Awareness for all POIs
     */
    public enableFieldAwareness(): void {
        if (!this.fieldContext) {
            Logger.Instance.Warn('🌪️ Field Context not available', 'Field');
            return;
        }
        
        this.layoutData.fieldAwarenessEnabled.value = true;
        
        // Set global field awareness state
        FieldContext.setGlobalFieldAwarenessEnabled(true);
        
        // IMPORTANT: Sync UI checkbox
        this.layoutData.fieldAwarenessEnabled.value = true;
        
        // Update field context with current POI count
        this.fieldContext.updateTotalCount(this.pois.length);
        
        // DEBUGGING: Ensure POI indices are set correctly and log field awareness status
        console.log('🌪️ ENABLING FIELD AWARENESS:');
        //console.log(`Total POIs: ${this.pois.length}`);
        //console.log(`Field Context set as global: ${!!FieldContext.getGlobalContext()}`);
        //console.log(`Global field awareness enabled: ${FieldContext.getGlobalFieldAwarenessEnabled()}`);
        
        this.pois.forEach((poi, index) => {
            // Ensure POI has proper index for field positioning
            poi.index = index;
            poi.setFieldIndex(index);
            
            //console.log(`POI ${index} (${poi.name}): field-aware=${poi.isFieldAware()}, index=${poi.index}`);
            
            // Log current position before field awareness
            const currentPos = poi.transform.position.value;
            //console.log(`Current position: ${currentPos?.x.toFixed(2)}, ${currentPos?.y.toFixed(2)}, ${currentPos?.z.toFixed(2)}`);
        });
        
        this.lastFieldUpdateTime = performance.now();
        
        //Logger.Instance.Info('🌪️ Field Awareness ENABLED', 'Field');
        this.eventManager.emit('field:awareness-enabled');
    }
    
    /**
     * Disable Field Awareness for all POIs
     */
    public disableFieldAwareness(): void {
        if (!this.fieldContext) return;
        
        this.layoutData.fieldAwarenessEnabled.value = false;
        
        // Set global field awareness state
        FieldContext.setGlobalFieldAwarenessEnabled(false);
        
        // IMPORTANT: Sync UI checkbox
        this.layoutData.fieldAwarenessEnabled.value = false;
        
        //Logger.Instance.Info('🌪️ Field Awareness DISABLED', 'Field');
        this.eventManager.emit('field:awareness-disabled');
    }
    
    /**
     * Toggle Field Awareness
     */
    public toggleFieldAwareness(): void {
        const currentState = this.layoutData.fieldAwarenessEnabled.value;
        //Logger.Instance.Info(`🌪️ Toggling Field Awareness from ${currentState} to ${!currentState}`, 'Field');
        
        if (currentState) {
            this.disableFieldAwareness();
        } else {
            this.enableFieldAwareness();
        }
    }
    
    /**
     * Create FieldParameters from current UI settings
     */
    private createFieldParametersFromUI(): FieldParameters {
        const params = new FieldParameters();
        
        // Defensive: Check if layoutData exists and has the properties
        if (!this.layoutData) {
            Logger.Instance.Warn('🌪️ layoutData not available, using default FieldParameters', 'Field');
            return params;
        }
        
        // Apply current UI values to field parameters - with safe defaults
        try {
            params.hoverAmplitude = this.layoutData.fieldHoverAmplitude?.value ?? 1.5;
            params.hoverFrequency = this.layoutData.fieldHoverFrequency?.value ?? 0.1;
            params.hoverPhaseShift = this.layoutData.fieldHoverPhaseShift?.value ?? 0.0;
            params.rotateAmplitude = this.layoutData.fieldRotateAmplitude?.value ?? 0.5;
            params.rotateFrequency = this.layoutData.fieldRotateFrequency?.value ?? 0.8;
            params.rotatePhaseShift = this.layoutData.fieldRotatePhaseShift?.value ?? 1.57;
            params.scaleAmplitude = this.layoutData.fieldScaleAmplitude?.value ?? 0.2;
            params.waveSpeed = this.layoutData.fieldWaveSpeed?.value ?? 1.5;
            params.windStrength = this.layoutData.fieldWindStrength?.value ?? 0.3;
            params.breathingIntensity = this.layoutData.fieldBreathingIntensity?.value ?? 1.0;
            params.organicVariation = this.layoutData.fieldOrganicVariation?.value ?? 0.3;
            params.fieldDirection = this.layoutData.fieldDirection?.value ?? new Vector3(0, 0, 1);
            params.cellSize = this.layoutData.fieldCellSize?.value ?? 2;
            params.cellSpacing = this.layoutData.fieldCellSpacing?.value ?? 0;
            params.customUpVector = this.layoutData.fieldCustomUpVector?.value ?? new Vector3(0, 1, 0);
            params.customAlignmentVector = this.layoutData.fieldCustomAlignmentVector?.value ?? new Vector3(0, 0, 1);
            
            // Convert UI string values to enum values
            const hoverDirectionValue = this.layoutData.fieldHoverDirection?.value ?? 'transversal';
            const alignmentModeValue = this.layoutData.fieldAlignmentMode?.value ?? 'aligned';
            const upVectorModeValue = this.layoutData.fieldUpVectorMode?.value ?? 'world-up';
            const rotationModeValue = this.layoutData.fieldRotationMode?.value ?? 'oscillating';
            const hoverDirectionOverrideValue = this.layoutData.fieldHoverDirectionOverride?.value ?? 'none';
            
            params.hoverDirection = hoverDirectionValue === 'transversal' ? HoverDirection.Transversal : HoverDirection.Longitudinal;
            params.alignmentMode = alignmentModeValue === 'aligned' ? AlignmentMode.Aligned : AlignmentMode.Perpendicular;
            params.upVectorMode = upVectorModeValue === 'world-up' ? UpVectorMode.WorldUp : UpVectorMode.PathUp;
            params.rotationMode = rotationModeValue === 'infinite' ? RotationMode.Infinite : RotationMode.Oscillating;
            params.hoverDirectionOverride = hoverDirectionOverrideValue as 'none' | 'vertical' | 'horizontal';
            
            //Logger.Instance.Info('🌪️ Created FieldParameters from UI settings', 'Field');
            console.log('🌪️ UI -> Field Parameters:', {
                hoverAmplitude: params.hoverAmplitude,
                hoverFrequency: params.hoverFrequency,
                alignmentMode: params.alignmentMode,
                hoverDirection: params.hoverDirection,
                hoverDirectionOverride: params.hoverDirectionOverride
            });
            
        } catch (error) {
            Logger.Instance.Error('🌪️ Error creating FieldParameters from UI settings, using defaults', 'Field');
            console.error('🌪️ UI -> Field Parameters error:', error);
        }
        
        return params;
    }
    
    /**
     * Apply field preset
     */
    public applyFieldPreset(preset: 'breathing-spiral' | 'ripple-grid' | 'snake-line' | 'organic-circle'): void {
        if (!this.fieldContext) {
            Logger.Instance.Warn('🌪️ Field Context not available', 'Field');
            return;
        }
        
        // Field context already has the needed configuration
        switch (preset) {
            case 'breathing-spiral':
                this.fieldContext.applyBreathingSpiralPreset();
                break;
            case 'ripple-grid':
                this.fieldContext.applyRippleGridPreset();
                break;
            case 'snake-line':
                this.fieldContext.applySnakeLinePreset();
                break;
            case 'organic-circle':
                this.fieldContext.createFormation('circle');
                break;
        }
        
        // Enable field awareness if not already enabled
        if (!this.layoutData.fieldAwarenessEnabled.value) {
            this.enableFieldAwareness();
        }
        
        //Logger.Instance.Info(`🌪️ Applied field preset: ${preset}`, 'Field');
        this.eventManager.emit('field:preset-applied', { preset });
    }
    
    /**
     * Get field status
     */
    public getFieldStatus(): any {
        if (!this.fieldContext) return null;
        
        return {
            enabled: this.layoutData.fieldAwarenessEnabled.value,
            poiCount: this.pois.length,
            fieldContext: this.fieldContext,
            systemStatus: this.fieldContext.getSystemStatus()
        };
    }

    // ==================== EXISTING METHODS (CONTINUE) ====================

    /**
     * Der einzige Zoom-Pfad der App.
     *
     * Drei Fehler steckten vorher zusammen im Zoom (Alex-Report 2026-08-03, „führt immer
     * zurück raus, egal in welche Richtung"):
     *
     * 1. DREI konkurrierende Implementierungen (wheel-Listener, handleZoomInput() im
     *    Update-Loop, InputManager.handleZoomInput()) — zwei davon mit umgekehrtem
     *    Vorzeichen zueinander. Pro Radrastung liefen bis zu drei Berechnungen
     *    gegeneinander.
     * 2. Alle lasen `cameraDistance.value` — das ist der INTERPOLIERTE Zwischenwert.
     *    Die Property hat 2 s Interpolationsdauer, der nächste Frame sieht also erst
     *    ~0,8 % der letzten Änderung. Jeder Leser rechnete damit faktisch auf dem alten
     *    Stand weiter und löschte das Ziel seines Vorgängers. Deshalb hier `targetValue`.
     * 3. Die Formeln waren asymmetrisch: Rauszoomen proportional und ungedeckelt,
     *    Reinzoomen zusätzlich gedeckelt UND halbiert. Über viele Rastungen driftete die
     *    Kamera dadurch systematisch nach außen. Jetzt: multiplikativ und spiegelsymmetrisch,
     *    gleicher Faktor in beide Richtungen.
     */
    public applyWheelZoom(deltaY: number): void {
        // Das Property drehen, das die Kamera TATSAECHLICH liest: auf Mobilgeraeten
        // gewinnt ein explizit gesetztes mobileCameraDistance ueber cameraDistance
        // (AdvancedPOICamera.getEffectiveCameraDistance). Wer dann cameraDistance
        // aendert, zoomt ins Leere — genau so brach der Pinch-Zoom, nachdem
        // mobileCameraDistance eine echte Einstellung wurde.
        const mobileProp = this.appSettings?.mobileCameraDistance;
        const useMobile = DeviceInfo.isMobile && ((mobileProp?.value ?? 0) as number) > 0;
        const prop = useMobile ? mobileProp : this.appSettings?.cameraDistance;
        if (!prop || !deltaY) return;

        // Vom ZIEL lesen, nicht vom laufenden Interpolationswert.
        const target = (prop.targetValue ?? prop.value ?? 3.8) as number;

        // Multiplikativ: konstante relative Änderung je Rastung, unabhängig vom Zoomlevel.
        // Trackpads liefern viele kleine deltaY, Mausräder wenige große — beides wird
        // gedämpft, damit eine Rastung nicht durch den ganzen Bereich springt.
        const step = Math.sign(deltaY) * Math.min(Math.abs(deltaY), 120) / 120; // -1 … +1
        const factor = Math.pow(1.18, step);   // rein/raus exakt spiegelbildlich

        const min = prop.boundsMin ?? 0.2;
        const max = prop.boundsMax ?? 20;
        const next = Math.max(min, Math.min(max, target * factor));

        if (Math.abs(next - target) > 0.0005) {
            prop.value = next;
        }
    }

    private handleZoomInput(): void {
        const inputManager = InputEventManager.Instance;
        
        // Debug: Only log when there's actual wheel input (remove random logging)
        if (inputManager.mouseWheelDelta !== 0) { 
            //Logger.Instance.Debug(`🔍 handleZoomInput: wheelDelta=${inputManager.mouseWheelDelta}`, 'Input');
        }
        
        // Check if there's mouse wheel input
        if (inputManager.mouseWheelDelta !== 0) {
            Logger.Instance.Info(`Zoom input: delta=${inputManager.mouseWheelDelta.toFixed(1)}`, 'Zoom');
            
            if (!this.appSettings?.cameraDistance) {
                Logger.Instance.Warn(`⚠️ Cannot zoom: appSettings.cameraDistance not available`, 'Input');
                return;
            }
            
            const wheelDelta = inputManager.mouseWheelDelta;
            const currentDistance = this.appSettings.cameraDistance.value ?? 3.8;

            // Zoom: proportional to current distance (feels natural at any zoom level)
            // Positive wheelDelta = zoom out (increase distance)
            // Negative wheelDelta = zoom in (decrease distance)
            const sensitivity = 0.008;
            const zoomFactor = wheelDelta * sensitivity * currentDistance;
            
            const newDistance = currentDistance + zoomFactor;
            
            // Clamp to the property's bounds (0.2 to 20 as defined in AppSettingsData)
            const minDistance = this.appSettings.cameraDistance.boundsMin ?? 0.2;
            const maxDistance = this.appSettings.cameraDistance.boundsMax ?? 20;
            const clampedDistance = Math.max(minDistance, Math.min(maxDistance, newDistance));
            
            // Only update if the value actually changed
            if (Math.abs(clampedDistance - currentDistance) > 0.001) {
                this.appSettings.cameraDistance.value = clampedDistance;
                Logger.Instance.Info(`Zoom: ${currentDistance.toFixed(2)} -> ${clampedDistance.toFixed(2)} (factor=${zoomFactor.toFixed(3)})`, 'Zoom');
                
                //Logger.Instance.Debug(`🔍 Zoom applied: wheel=${wheelDelta.toFixed(0)}, distance=${currentDistance.toFixed(2)} → ${clampedDistance.toFixed(2)}`, 'Input');
            } else {
                //Logger.Instance.Debug(`🔍 Zoom ignored: minimal change or at bounds`, 'Input');
            }
        }
    }

    private _resetPerformanceStats(): void {
        this._performanceStats.frameCount = 0;
        this._performanceStats.lastTime = performance.now();
        this._performanceStats.updateTime = 0;
        this._performanceStats.renderTime = 0;
        this._performanceStats.meshUpdates = 0;
        this._performanceStats.materialUpdates = 0;
        this._performanceStats.transformUpdates = 0
    };

    private _lastLogTime: number = 0;
    private updateStatsOverlay(): void {
        const now = Date.now();
        if (now - this._lastLogTime < 2000) { // Update every 250ms
            return;
        }
        this._lastLogTime = now;

        if (!this._statsOverlay) {
            return;
        }

        if (this.sceneManager && this.scene) {
            const stats = this.sceneManager.getStats();
            
            const nodesTree = this.buildNodeTree(this.sceneManager.getAllRootNodes());

            let content = `<b>SceneNodeManager (${stats.totalNodes} root nodes):</b><br/>${nodesTree}`;
            
            // Add frustum culling statistics
            const frustumStats = stats.frustumCulling;
            content += `<br/><b>Frustum Culling:</b><br/>`;
            content += `&nbsp;&nbsp;Status: ${frustumStats.isEnabled ? '<span style="color: #4CAF50">ENABLED</span>' : '<span style="color: #F44336">DISABLED</span>'}<br/>`;
            if (frustumStats.isEnabled && frustumStats.totalObjects > 0) {
                content += `&nbsp;&nbsp;Objects: ${frustumStats.totalObjects}<br/>`;
                content += `&nbsp;&nbsp;Visible: ${frustumStats.visibleObjects}<br/>`;
                content += `&nbsp;&nbsp;Culled: ${frustumStats.culledObjects}<br/>`;
                content += `&nbsp;&nbsp;Cull Rate: ${frustumStats.cullRate.toFixed(1)}%<br/>`;
                
                // Color code the cull rate
                if (frustumStats.cullRate > 50) {
                    content += `&nbsp;&nbsp;<span style="color: #4CAF50">🎯 High culling efficiency</span><br/>`;
                } else if (frustumStats.cullRate > 20) {
                    content += `&nbsp;&nbsp;<span style="color: #FF9800">⚠️ Moderate culling</span><br/>`;
                } else {
                    content += `&nbsp;&nbsp;<span style="color: #F44336">⚡ Low culling (most objects visible)</span><br/>`;
                }
            }

            this._statsOverlay.innerHTML = content;

        } else {
            this._statsOverlay.innerHTML = 'Stats: waiting...';
        }
    }

    private buildNodeTree(nodes: SceneNode[], depth = 0): string {
        let treeHtml = '';
        const indent = '&nbsp;&nbsp;'.repeat(depth);

        for (const node of nodes) {
            // Use duck typing instead of instanceof to avoid type conflicts
            const isPoi = node.constructor.name === 'POI' || (node as any).shape !== undefined;
            const icon = isPoi ? '📍' : '📦';
            const nodeClassName = `<i>(${node.constructor.name})</i>`;
            treeHtml += `${indent}${icon} ${node.name} ${nodeClassName}<br/>`;

            // Also show the three.js mesh associated with this node
            const threeMesh = (node as any)._mesh as THREE.Object3D;
            if (threeMesh) {
                treeHtml += `${indent}&nbsp;&nbsp;└─ <i>(${threeMesh.type})</i><br/>`;
            }

            // Recurse into children
            if (node.getChildren().length > 0) {
                treeHtml += this.buildNodeTree(node.getChildren(), depth + 1);
            }
        }
        return treeHtml;
    }

    private _updateHoverState(deltaTime: number): void {
        const mousePos = InputEventManager.Instance.mousePosition;

        // Check if the mouse has moved
        if (!mousePos.equals(this._lastMousePositionForHover)) {
            // Mouse has moved, reset everything
            this._hoverTimer = 0;
            this._lastMousePositionForHover.copy(mousePos);
            this._isTooltipRaycastDone = false;
            
            if (this._tooltipPOI) {
                this.eventManager.emit('ui:hide-tooltip');
                this._tooltipPOI = null;
            }
            return;
        }

        // Mouse is stationary, increment timer
        this._hoverTimer += deltaTime;

        // If timer exceeds threshold and we haven't already checked this position
        if (this._hoverTimer >= EngineDemoApp.HOVER_TIME_TO_SHOW_TOOLTIP && !this._isTooltipRaycastDone) {
            this._isTooltipRaycastDone = true; // Mark that we've done the check for this spot

            const poiUnderCursor = this.getPOIFromScreenPoint(mousePos.x, mousePos.y);

            if (poiUnderCursor) {
                this._tooltipPOI = poiUnderCursor;
                const content = this._getTransformTooltipContent(this._tooltipPOI.transform);
                
                this.eventManager.emit('ui:show-tooltip', {
                    content,
                    x: mousePos.x,
                    y: mousePos.y,
                });
            }
        }
    }

    private _getTransformTooltipContent(transform: Transform): string {
        const p = transform.position;
        const r = transform.rotation;
        const s = transform.scale;

        const formatVec3 = (v: Vector3 | null) => v ? `(${v.x.toFixed(2)}, ${v.y.toFixed(2)}, ${v.z.toFixed(2)})` : 'null';
        const formatQuat = (q: Quaternion | null) => q ? `(${q.x.toFixed(2)}, ${q.y.toFixed(2)}, ${q.z.toFixed(2)}, ${q.w.toFixed(2)})` : 'null';

        const getPropDetails = <T extends Vector3 | Quaternion>(prop: SettingsProperty<T>, name: string): string => {
            let valueStr = '';
            if (prop.value instanceof Vector3) {
                valueStr = formatVec3(prop.value);
            } else if (prop.value instanceof Quaternion) {
                valueStr = formatQuat(prop.value);
            }

            let details = `<b>${name}</b>: ${valueStr}<br>`;
            if (prop instanceof InterpolatedProperty) {
                let targetStr = '';
                if (prop.targetValue instanceof Vector3) {
                    targetStr = formatVec3(prop.targetValue);
                } else if (prop.targetValue instanceof Quaternion) {
                    targetStr = formatQuat(prop.targetValue);
                }
                details += `  Type: Interpolated<br>`;
                details += `  Target: ${targetStr}<br>`;
                details += `  Animating: ${prop.isInterpolating ? 'Yes' : 'No'}<br>`;
            } else {
                details += `  Type: Simple<br>`;
            }
            return details;
        };

        let content = getPropDetails(p, 'Position');
        content += getPropDetails(r, 'Rotation');
        content += getPropDetails(s, 'Scale');

        return content;
    }

    // Cleanup logic
    protected onDispose(): void {
        // Clean up testing utilities
        if (this.sceneNodeTransformTest) {
            this.sceneNodeTransformTest.dispose();
        }
        
        this.cleanupKeyHandlers();
        this.cleanupMouseHandlers();
        
        if (this._statsOverlay) {
            this.removeStatsOverlay();
        }
        
        if (this._performanceOverlay) {
            this.removePerformanceOverlay();
        }
        
        // Clean up gizmo
        this.removeGizmo();
        
        // Clean up debug overlay
        DebugOverlay.getInstance().destroy();
        
        // Clean up AR manager
        this.arManager?.dispose();
        
        // Clean up AR UI elements
        this.removeARButton();
        this.hideAROverlay();
        
        //Logger.Instance.Info("🧹 EngineDemoApp disposed", "Disposal");
    }

    // --- Public methods for the UI to call ---

    // HTML Canvas rendering toggle
    private _htmlCanvasRenderingEnabled = true;

    public toggleHtmlCanvasRendering(): void {
        this._htmlCanvasRenderingEnabled = !this._htmlCanvasRenderingEnabled;
        //console.log(`🎨 HTML Canvas Rendering ${this._htmlCanvasRenderingEnabled ? 'ENABLED' : 'DISABLED'}`);
        
        // Update all POIs with canvas labels
        this.pois.forEach(poi => {
            const labelMesh = (poi as any)._labelMesh;
            if (labelMesh && typeof labelMesh.setHtmlRenderingEnabled === 'function') {
                labelMesh.setHtmlRenderingEnabled(this._htmlCanvasRenderingEnabled);
            }
        });
    }

    // Performance profiling - now using centralized TestingConfig
    private _performanceStats = {
        frameCount: 0,
        lastTime: performance.now(),
        updateTime: 0,
        renderTime: 0,
        meshUpdates: 0,
        materialUpdates: 0,
        transformUpdates: 0
    };

    public togglePerformanceProfile(): void {
        TestingConfig.performance = !TestingConfig.performance;
        //console.log(`🔬 Performance profiling ${TestingConfig.performance ? 'ENABLED' : 'DISABLED'}`);
        
        if (TestingConfig.performance) {
            this._performanceStats.frameCount = 0;
            this._performanceStats.lastTime = performance.now();
            this._performanceOverlayUpdateTime = 0;
            this.createPerformanceOverlay();
            //console.log(`🎮 Controls: P = toggle profiling`);
        } else {
            this._logPerformanceReport();
            this.removePerformanceOverlay();
        }
    }

    private _logPerformanceReport(): void {
        const duration = (performance.now() - this._performanceStats.lastTime) / 1000;
        const avgFps = this._performanceStats.frameCount / duration;
        
        //console.log(`📊 Performance Report (${duration.toFixed(1)}s):`);
        //console.log(`   FPS: ${avgFps.toFixed(1)}`);
        //console.log(`   Total POIs: ${this.pois.length}`);
        //console.log(`   Mesh Updates: ${this._performanceStats.meshUpdates}`);
        //console.log(`   Material Updates: ${this._performanceStats.materialUpdates}`);
        //console.log(`   Transform Updates: ${this._performanceStats.transformUpdates}`);
    }

    // Performance overlay methods
    private createPerformanceOverlay(): void {
        if (this._performanceOverlay) {
            return; // Already exists
        }

        this._performanceOverlay = document.createElement('div');
        this._performanceOverlay.id = 'performance-overlay';
        this._performanceOverlay.style.cssText = `
            position: fixed;
            top: 10px;
            right: 10px;
            background: rgba(0, 0, 0, 0.9);
            color: #00ff00;
            font-family: 'Courier New', monospace;
            font-size: 12px;
            padding: 12px;
            border-radius: 8px;
            border: 2px solid #00ff00;
            z-index: 1000;
            min-width: 220px;
            max-width: 280px;
            white-space: pre-line;
            backdrop-filter: blur(4px);
            box-shadow: 0 4px 12px rgba(0, 255, 0, 0.2);
        `;
        
        this._performanceOverlay.innerHTML = 'Performance Monitor Starting...';
        document.body.appendChild(this._performanceOverlay);
    }

    private removePerformanceOverlay(): void {
        if (this._performanceOverlay) {
            document.body.removeChild(this._performanceOverlay);
            this._performanceOverlay = null;
        }
    }

    private updatePerformanceOverlay(): void {
        if (!this._performanceOverlay || !TestingConfig.performance) {
            return;
        }

        const now = performance.now();
        if (now - this._performanceOverlayUpdateTime < 1000) {
            return; // Update only every second
        }
        this._performanceOverlayUpdateTime = now;

        const duration = (now - this._performanceStats.lastTime) / 1000;
        const currentFps = duration > 0 ? this._performanceStats.frameCount / duration : 0;
        const avgUpdateTime = this._performanceStats.frameCount > 0 ? 
            this._performanceStats.updateTime / this._performanceStats.frameCount : 0;

        // Calculate memory info if available
        const memInfo = (performance as any).memory ? {
            used: Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024),
            total: Math.round((performance as any).memory.totalJSHeapSize / 1024 / 1024),
            limit: Math.round((performance as any).memory.jsHeapSizeLimit / 1024 / 1024)
        } : null;

        // Color code FPS for easy visual assessment
        const fpsColor = currentFps >= 55 ? '#00ff00' : 
                        currentFps >= 30 ? '#ffff00' : 
                        currentFps >= 15 ? '#ff8800' : '#ff0000';

        const content = `🔬 PERFORMANCE MONITOR

<span style="color: ${fpsColor}">FPS: ${currentFps.toFixed(1)}</span>
Frame Time: ${(1000/currentFps).toFixed(2)}ms
Update Time: ${avgUpdateTime.toFixed(2)}ms/frame

Scene Info:
└─ POIs: ${this.pois.length}
└─ Frames: ${this._performanceStats.frameCount}
└─ Runtime: ${duration.toFixed(1)}s

${memInfo ? `Memory:
└─ Used: ${memInfo.used}MB / ${memInfo.total}MB
└─ Limit: ${memInfo.limit}MB

` : ''}Press P to stop profiling`;

        this._performanceOverlay.innerHTML = content;
    }

    /**
     * Export the current runtime state (global settings + all POIs) back to markdown.
     * Use this to persist user-tweaked settings into the saved presentation file.
     */
    public async exportToMarkdown(): Promise<string> {
        const exporter = new MarkdownExporter();
        return exporter.export({
            appSettings: this.appSettings,
            layoutData: this.layoutData,
            pois: this.pois
        });
    }

    /** Kamera auf den aktuellen Annotationspunkt des Overlays fahren. */
    private applyAnnotationFocus(): void {
        const world = this.annotationOverlay.currentWorldPosition();
        if (!world || !this.camera) return;
        const base = (this.appSettings?.cameraDistance.value ?? 4) as number;
        this.camera.setAnnotationFocus(world, Math.max(0.7, base * 0.35));
        Logger.Instance.Info(
            `Annotation ${this.annotationOverlay.index + 1}/${this.annotationOverlay.count}: ${this.annotationOverlay.current?.label ?? '?'}`,
            'Annotations');
    }

    public nextPOI(): void {
        // Hat die aktive POI Annotationspunkte, laufen next/prev erst durch diese
        // hindurch (MeshPoint-Hopping) und wechseln erst danach zur naechsten POI.
        if (this.annotationOverlay.focusNext()) {
            this.applyAnnotationFocus();
            return;
        }
        if (this.camera) this.camera.setAnnotationFocus(null);
        if (this.pois.length === 0) {
            Logger.Instance.Info('nextPOI: No POIs available', 'POI');
            return;
        }

        const currentPOI = this.systemState.activePOI;
        const currentIndex = currentPOI ? this.pois.indexOf(currentPOI as any) : -1;

        // If current POI not found by reference, fall back to name match
        let actualCurrentIndex = currentIndex;
        if (currentIndex === -1 && currentPOI) {
            const poiByName = this.pois.findIndex(p => p.name === currentPOI.name);
            if (poiByName !== -1) {
                actualCurrentIndex = poiByName;
                Logger.Instance.Warn(`nextPOI: current POI not in array by ref, fell back to name match at index ${actualCurrentIndex}`, 'POI');
            }
        }

        const nextIndex = (actualCurrentIndex + 1) % this.pois.length;
        const nextPOI = this.pois[nextIndex];

        Logger.Instance.Info(`nextPOI: ${currentPOI?.name ?? 'none'}(${currentIndex}) -> ${nextPOI?.name ?? 'none'}(${nextIndex}) of ${this.pois.length}`, 'POI');

        this.setActivePOI(nextPOI as any);
    }

    public previousPOI(): void {
        if (this.annotationOverlay.focusPrev()) {
            this.applyAnnotationFocus();
            return;
        }
        if (this.camera) this.camera.setAnnotationFocus(null);
        if (this.pois.length === 0) {
            Logger.Instance.Info('previousPOI: No POIs available', 'POI');
            return;
        }

        const currentPOI = this.systemState.activePOI;
        const currentIndex = currentPOI ? this.pois.indexOf(currentPOI as any) : -1;
        const prevIndex = currentIndex <= 0 ? this.pois.length - 1 : currentIndex - 1;
        const prevPOI = this.pois[prevIndex];

        Logger.Instance.Info(`previousPOI: ${currentPOI?.name ?? 'none'}(${currentIndex}) -> ${prevPOI?.name ?? 'none'}(${prevIndex}) of ${this.pois.length}`, 'POI');

        this.setActivePOI(prevPOI as any);
    }

    /**
     * Get total POI count for UI
     */
    public getPOICount(): number {
        return this.pois.length;
    }

    /**
     * Get the active VideoMaterial of the currently active POI, if any.
     * Used by auto-play to gate next-advance until video finishes once.
     */
    public getCurrentPOIVideoInfo(): { videoEl: HTMLVideoElement | null; durationSec: number } | null {
        const poi = this.systemState.activePOI as any;
        if (!poi) return null;
        const materials = poi.getAllMaterials ? poi.getAllMaterials() : [];
        for (const m of materials) {
            if (m && m.constructor && m.constructor.name === 'VideoMaterial') {
                // duration getter exists on VideoMaterial; _video is private but accessible via cast
                const videoEl = (m as any)._video as HTMLVideoElement | null;
                const durationSec = typeof m.duration === 'number' ? m.duration : 0;
                return { videoEl, durationSec };
            }
        }
        return null;
    }

    /**
     * Get current POI index (1-based) for UI
     */
    public getCurrentPOIIndex(): number {
        if (this.pois.length === 0) return 0;
        
        const currentPOI = this.systemState.activePOI;
        if (!currentPOI) return 1; // Default to first POI
        
        const currentIndex = this.pois.indexOf(currentPOI as any);
        return currentIndex >= 0 ? currentIndex + 1 : 1;
    }

    /**
     * Get current POI object for UI
     */
    public getCurrentPOI(): POI | null {
        return this.systemState.activePOI as POI || null;
    }

    protected setActivePOI(poi: POI | null): void {
        if (!poi) {
            Logger.Instance.Error('setActivePOI called with null/invalid POI', 'POI');
            return;
        }

        if (!this.camera) {
            Logger.Instance.Error(`setActivePOI(${poi.name}): camera not available`, 'POI');
            return;
        }

        const previousPOI = this.systemState.activePOI;
        Logger.Instance.Info(`setActivePOI: ${previousPOI?.name ?? 'none'} -> ${poi.name}`, 'POI');

        // Deactivate animation on previous active POI
        const previousActivePOI = this.systemState.activePOI;
        if (previousActivePOI && previousActivePOI.shape) {
            const previousShape = previousActivePOI.shape as any;
            if (previousShape.shapeAnimator && previousShape.shapeAnimator.enableWiggle) {
                // Disable wiggle animation
                previousShape.shapeAnimator.enableWiggle.setImmediate(false);

                // ⚠️ CORRECT: Reset shape transform to IDENTITY (not POI transform!)
                // Shape transform should be identity + local animation offsets
                if (previousShape.transform) {
                    previousShape.transform.position.setImmediate(new Vector3(0, 0, 0));
                    previousShape.transform.rotation.setImmediate(new Quaternion());
                    previousShape.transform.scale.setImmediate(new Vector3(1, 1, 1));
                }
            }
        }

        this.systemState.setActivePOI(poi);
        // Annotations-Overlay auf die neue POI umschalten (laedt async, zeigt Marker)
        this.camera?.setAnnotationFocus(null);
        void this.annotationOverlay.setActivePoi(poi);

        const newActivePOI = this.systemState.activePOI;
        if (newActivePOI !== poi) {
            Logger.Instance.Warn(`setActivePOI: systemState mismatch — set ${poi.name}, got ${newActivePOI?.name ?? 'none'}`, 'POI');
        }

        this.camera.setTarget(poi);
        Logger.Instance.Info(`setActivePOI: camera.setTarget(${poi.name}) called`, 'POI');

        // (DOF auto-focus moved to onUpdate so it runs every frame.)

        // Emit UI sync event so overlays can update (e.g., 1/9 counter)
        try {
            const indexForUI = this.getCurrentPOIIndex();
            const totalForUI = this.getPOICount();
            this.eventManager.emit('ui:poi-changed', { index: indexForUI, total: totalForUI, poi });
        } catch {}
        
        // Activate animation on new active POI
        if (poi && poi.shape) {
            const shape = poi.shape as any;
            if (shape.shapeAnimator && shape.shapeAnimator.enableWiggle) {
                // ⚠️ CORRECT: Ensure shape starts from IDENTITY before animation
                // Local animations work on identity transform + offsets
                if (shape.transform) {
                    shape.transform.position.setImmediate(new Vector3(0, 0, 0));
                    shape.transform.rotation.setImmediate(new Quaternion());
                    shape.transform.scale.setImmediate(new Vector3(1, 1, 1));
                }
                
                // Start wiggle animation - DISABLED to turn off automatic wiggle
                // shape.shapeAnimator.enableWiggle.setImmediate(true);
            }
        }
        
        console.log('🔍 setActivePOI: Completed POI change process');
    }

    private async loadPoisFromMarkdown(): Promise<void> {
        console.log("🔥 loadPoisFromMarkdown() method called!");
        //Logger.Instance.Info("🔥 loadPoisFromMarkdown() method started", "Initialization");
        
        this.clearAllPOIs();
        console.log("🧹 Cleared all existing POIs");
        console.log("📄 EngineDemoApp: creating POIs from markdown...");
        //Logger.Instance.Info("📄 Starting to fetch and parse test_pres.md", "Initialization");
        
        try {
            const response = await fetch('/presentations/test_field_pres copy.md');
            if (!response.ok) {
                throw new Error(`Failed to fetch test_field_pres copy.md: ${response.statusText}`);
            }
            const markdown = await response.text();

            const parser = new MarkdownParser();
            const poiDataInstances = await parser.parse(markdown, this.sceneSettings, this.appSettings, this.layoutData);

            const pois = poiDataInstances.map((poiDataInstance: POIData) => {
                // Extract title from the POIData instance
                const name = this.extractTitleFromPOIData(poiDataInstance);
                return new POI(name, poiDataInstance, this.sceneSettings, this.appSettings, this.layoutData);
            });
            
            //console.log(`📍 Adding ${pois.length} POIs to scene...`);
            this._isBulkLoading = true;
            try {
                pois.forEach((poi: POI) => this.addPOI(poi));
            } finally {
                this._isBulkLoading = false;
            }
            // One layout pass at the end now that the array is fully populated
            if (this.layout && !this.layoutData.fieldAwarenessEnabled.value && this.pois.length > 0) {
                this.applyLayoutWithProcessCurveUpdate();
            }

            //console.log(`✅ EngineDemoApp: successfully created ${pois.length} POIs from markdown.`);
            //Logger.Instance.Info(`✅ Successfully created ${pois.length} POIs from test_pres.md`, "Initialization");
            
            if (this.pois.length > 0) {
                // Defer auto-focusing camera until initial model load + layout completes
                // this.setActivePOI(this.pois[0]);
            }
            
        } catch (error) {
            console.error('❌ ERROR in loadPoisFromMarkdown:', error);
            Logger.Instance.Error(`❌ Failed to load POIs from markdown: ${error instanceof Error ? error.message : String(error)}`, "Initialization");
       }
    }

    /**
     * Extract title from POIData instance by parsing the markdown content
     */
    private extractTitleFromPOIData(poiData: POIData): string {
        const markdown = poiData.markdown.value || '';
        
        // Try to extract title from markdown (look for first heading)
        const titleMatch = markdown.match(/^#\s+(.+)$/m);
        if (titleMatch) {
            return titleMatch[1];
        }
        
        // Fallback to generic name
        return 'POI';
    }

    protected addPOI(poi: POI): void {
        this.pois.push(poi);
        
        // ⚠️ FIX: Ensure POI index matches array position for proper next/prev navigation
        if (poi.index !== this.pois.length - 1) {
            poi.index = this.pois.length - 1;
            //Logger.Instance.Debug(`🔧 Fixed POI ${poi.id} index to ${poi.index} for proper navigation`, 'POI');
        }
        
        this.sceneManager?.addPOI(poi);

        // Newly-added POI's geometry/material likely not finalized yet (async texture loads,
        // GLB models, etc.). Mark layout dirty so deferred reapply will run once it settles.
        this._layoutDirty = true;

        // Keep UI nav state (x/y counter, next/prev button enablement) in sync when POIs are
        // added asynchronously after init — e.g. MapPinsApp creates 45 pins from the GLB scan
        // long after the UI read its initial getPOICount(). Without this the next-button stays
        // disabled because the UI still believes totalPOIs=1.
        try {
            this.eventManager.emit('ui:poi-changed', {
                index: this.getCurrentPOIIndex(),
                total: this.pois.length,
                poi: this.systemState.activePOI
            });
        } catch {}

        // POIs self-manage field awareness - no need for field manager POI management

        // ✅ Apply layout to all POIs using reference-based approach (only if field awareness is disabled)
        // Skip during bulk loading — caller will trigger one layout pass at the end.
        if (this.layout && !this.layoutData.fieldAwarenessEnabled.value && !this._isBulkLoading) {
            this.applyLayoutWithProcessCurveUpdate();
        }

        //Logger.Instance.Debug(`POI ${poi.id} added to app and scene at index ${poi.index}`, 'POI');
    }

    public getPOI(id: string): POI | undefined {
        return this.pois.find(p => p.id === id);
    }

    public clearAllPOIs(): void {
        // POIs self-manage field awareness - no need for field manager cleanup
        
        this.pois.forEach(poi => {
            // Remove the logical node from the manager
            this.sceneManager?.removeSceneNode(poi);
        });
        this.pois = [];
        this.systemState.setActivePOI(null);
        
        // Reset test POI tracking
        this.testPOIBatchCounter = 0;
        this.hasTestPOIs = false;
        
        //Logger.Instance.Info('🧹 Cleared all POIs from app and scene', 'POI');
    }

    public getObjectPropertiesFromScreenPoint(x: number, y: number): SettingsProperty<any>[] | null {
        if (!this.camera || !this.scene) return null;

        const threeCamera = this.scene.getCamera();
        const mouse = new THREE.Vector2(x, y);
        this.raycaster.setFromCamera(mouse, threeCamera);

        const visibleObjects = this.sceneManager?.getVisibleThreeJSObjects(threeCamera);
        if (!visibleObjects) return null;
        
        const intersects = this.raycaster.intersectObjects(visibleObjects);
        
        if (intersects.length > 0) {
            const firstHit = intersects[0].object;
            const sceneNode = firstHit.userData.sceneNode as Mesh3D;
            if (sceneNode) {
                //Logger.Instance.Debug(`Raycast hit: ${sceneNode.name}`);
                return this._getPropertiesFromObject(sceneNode);
            }
        }

        //Logger.Instance.Debug('Raycast miss');
        return null;
    }

    public getPOIFromScreenPoint(x: number, y: number): POI | null {
        if (!this.camera || !this.scene || !this.canvas) return null;

        // Normalize mouse coordinates from viewport to NDC (-1 to +1)
        const rect = this.canvas.getBoundingClientRect();
        const normalizedX = ((x - rect.left) / rect.width) * 2 - 1;
        const normalizedY = -((y - rect.top) / rect.height) * 2 + 1;
        
        //Logger.Instance.Debug(`Raycasting at screen(x:${x}, y:${y}) -> ndc(x:${normalizedX.toFixed(2)}, y:${normalizedY.toFixed(2)})`);

        const threeCamera = this.scene.getCamera();
        const mouse = new THREE.Vector2(normalizedX, normalizedY);
        this.raycaster.setFromCamera(mouse, threeCamera);

        const visibleObjects = this.sceneManager?.getVisibleThreeJSObjects(threeCamera);
        if (!visibleObjects) {
            //Logger.Instance.Debug('Raycast cannot proceed: no visible objects in SceneManager.');
            return null;
        }

        //Logger.Instance.Debug(`Raycasting against ${visibleObjects.length} objects.`);
        const intersects = this.raycaster.intersectObjects(visibleObjects, true); // Recursive check
        
        if (intersects.length > 0) {
            // Check each intersection in order (closest first)
            for (const intersection of intersects) {
                const hitObject = intersection.object;
                //Logger.Instance.Debug(`Raycast hit: ${hitObject.name} (type: ${hitObject.type})`);

                // Traverse up to find the SceneNode, which should be a POI's shape
                let current: THREE.Object3D | null = hitObject;
                while (current) {
                    const sceneNode = current.userData.sceneNode;
                    
                    // Check if this is a Mesh3D that belongs to a POI
                    if (sceneNode && typeof sceneNode === 'object' && 'poi' in sceneNode) {
                        const mesh3D = sceneNode as any; // Cast to avoid type issues
                        
                        // Verify this is actually a POI by checking if it's in our POI list
                        const poi = mesh3D.poi;
                        if (poi && this.pois.includes(poi)) {
                            //Logger.Instance.Debug(`✅ Valid POI hit: ${poi.name || poi.id}`);
                            return poi as POI;
                        } else {
                            //Logger.Instance.Debug(`❌ Invalid POI hit: object has poi property but not in POI list`);
                        }
                    }
                    current = current.parent;
                }
                
                //Logger.Instance.Debug(`❌ Hit object ${hitObject.name} but no valid POI found in hierarchy`);
            }
        }

        //Logger.Instance.Debug('❌ No POI hit detected');
        return null;
    }

    public getGlobalSettingsForUI(): SettingsProperty<any>[] {
        const globalProps = [
            ...this._getPropertiesFromObject(this.appSettings),
            ...this._getPropertiesFromObject(this.sceneSettings),
            ...this._getPropertiesFromObject(this.layoutData)
        ];
        return globalProps;
    }
    
    private _getPropertiesFromObject(obj: any): SettingsProperty<any>[] {
        // Skip POI-specific handling for now to avoid type conflicts
        // The enhanced POI system handles this differently
        if (!obj) return [];
        
        const properties = new Set<SettingsProperty<any>>();
        
        const findProperties = (currentObj: any) => {
            if (!currentObj || typeof currentObj !== 'object') return;

             for (const key in currentObj) {
                 if (Object.prototype.hasOwnProperty.call(currentObj, key)) {
                     const value = currentObj[key];

                     if (value instanceof SettingsProperty) {
                         properties.add(value);
                     } else if (value !== null && typeof value === 'object' && !key.startsWith('_')) {
                         findProperties(value);
                     }
                 }
             }
        };

        findProperties(obj);
        return Array.from(properties);
    }
    
    // MOVED TO DebugManager.ts: lines 1816-1818

    private handleKeyDown = (event: KeyboardEvent): void => {
        const activeElement = document.activeElement;
        const isInputFocused = activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA');

        // If an input field is focused, do not trigger global keyboard shortcuts like POI switching.
        if (isInputFocused) {
            return;
        }

        // We only prevent the default browser action for keys we are actively handling.
        // This is now handled by the InputEventManager.
        switch (event.key) {
            // POI navigation is now handled by InputManager - removed duplicate handlers
            // case 'ArrowLeft':
            //     this.previousPOI();
            //     break;
            // case 'ArrowRight':
            //     this.nextPOI();
            //     break;
            
            // Layout System Testing Shortcuts
            case '1':
                this.createTestPOIs(3);
                break;
            case '2':
                this.createTestPOIs(5);
                break;
            case '3':
                this.createTestPOIs(8);
                break;
            case '4':
                // If no test POIs exist yet, create the initial batch
                // Otherwise add more POIs to test performance scaling
                if (!this.hasTestPOIs) {
                    this.createTestPOIs(12);
                } else {
                    this.addMoreTestPOIs(12);
                }
                break;
            case '5':
                this.createHexagonTestPOIs(20);
                break;
            case '6':
                this.createMassivePOITest(100);
                break;
            case 'g':
                this.setLayouter('grid');
                break;
            case 'b':
                this.appSettings.showBounds.value=!this.appSettings.showBounds.value;
                break;


            case 'S':
                if (event.shiftKey) {
                    // First check if we can toggle process curve marker spheres
                    if (this.pois.length > 0) {
                        const sceneSettings = this.pois[0].getSceneSettings();
                        if (sceneSettings) {
                            sceneSettings.showProcessMarkerSpheres.value = !sceneSettings.showProcessMarkerSpheres.value;
                            //console.log(`🔮 Process Marker Spheres: ${sceneSettings.showProcessMarkerSpheres.value ? 'ENABLED' : 'DISABLED'}`);
                            // Trigger layout update to refresh process curve
                            if (this.layout) {
                                this.layout.updateProcessCurve(this.pois, sceneSettings);
                            }
                        }
                    } else {
                        // If no POIs, set field layout to spiral instead
                        this.setFieldLayout('spiral');
                    }
                }
                break;
            case 'T':
                if (event.shiftKey) {
                    // Toggle Process Curve Start Marker (Shift+T)
                    if (this.pois.length > 0) {
                        const sceneSettings = this.pois[0].getSceneSettings();
                        if (sceneSettings) {
                            sceneSettings.showProcessStartMarker.value = !sceneSettings.showProcessStartMarker.value;
                            //console.log(`🚀 Process Start Marker: ${sceneSettings.showProcessStartMarker.value ? 'ENABLED' : 'DISABLED'}`);
                            // Trigger layout update to refresh process curve
                            if (this.layout) {
                                this.layout.updateProcessCurve(this.pois, sceneSettings);
                            }
                        }
                    }
                } else {
                    // Traditional Layout Orientation Test - creates POIs + line layout WITHOUT field awareness
                    console.log('🔧 Starting TRADITIONAL LAYOUT orientation test...');
                    
                    // STEP 1: DISABLE Field Awareness (critical for traditional layout testing)
                    if (this.layoutData.fieldAwarenessEnabled.value) {
                        console.log('🌪️ Disabling Field Awareness for traditional layout test');
                        this.disableFieldAwareness();
                    }
                    
                    // STEP 2: Reset any existing POI transforms first
                    if (this.pois.length > 0) {
                        this.resetAllPOITransforms();
                    }
                    
                    // STEP 3: Create test POIs with clear orientation shapes
                    this.createTestPOIs(5);
                    
                    // STEP 4: Apply TRADITIONAL line layout (not field layout)
                    setTimeout(() => {
                        this.setLayouter('line');
                        console.log('✅ Traditional Layout Test: POIs created with LINE layout, Field Awareness DISABLED');
                    }, 100);
                }
                break;                
            case 'c':
                this.setLayouter('circle');
                break;
            case 'f':
                // Toggle frustum culling
                this.appSettings.enableFrustumCulling.value = !this.appSettings.enableFrustumCulling.value;
                //console.log(`🎭 Frustum culling: ${this.appSettings.enableFrustumCulling.value ? 'ENABLED' : 'DISABLED'}`);
                break;
            case 's':
                this.setLayouter('spiral');
                break;
            case 'n':
                this.setLayouter('line');
                break;
            case 'm':
                // 🚀 Test Modern Layouters from TestField
                this.testModernLayouters();
                break;
            case 'r':
                this.setLayouter('horizontal');
                break;
            case 'R':
                // NEW: Quick transform reset if user encounters scale artifacts
                console.log('🔧 Manual transform reset requested...');
                this.resetAllPOITransforms();
                console.log('✅ All POI transforms have been reset to clean state');
                break;
            case 'v':
                this.setLayouter('vertical');
                break;
            case 'u':
                this.setLayouter('uphill');
                break;
            case 'x':
                this.setLayouter('hexagonal');
                break;
            case 'o':
                this.setLayouter('spherical_hexagonal');
                break;
            case 'p':
                // Toggle Process Curve (lowercase p)
                if (!event.shiftKey) {
                    if (this.pois.length > 0) {
                        const sceneSettings = this.pois[0].getSceneSettings();
                        if (sceneSettings) {
                            sceneSettings.showProcessCurve.value = !sceneSettings.showProcessCurve.value;
                            //console.log(`🛤️ Process Curve: ${sceneSettings.showProcessCurve.value ? 'ENABLED' : 'DISABLED'}`);
                            // Trigger layout update to refresh process curve
                            if (this.layout) {
                                this.layout.updateProcessCurve(this.pois, sceneSettings);
                            }
                        }
                    }
                }
                break;
            case 'P':
                this.togglePerformanceProfile();
                break;
            case 'h':
            case 'H':
                this.toggleHtmlCanvasRendering();
                break;
            case 'F':
                this.toggleFieldAwareness();
                break;
            case 'Q':
                this.applyFieldPreset('breathing-spiral');
                break;
            case 'W':
                this.applyFieldPreset('ripple-grid');
                break;
            case 'E':
                this.applyFieldPreset('snake-line');
                break;
            case 'I':
                this.applyFieldPreset('organic-circle');
                break;
            case 'L':
                if (event.shiftKey) {
                    this.setFieldLayout('line');
                }
                break;
            case 'G':
                if (event.shiftKey) {
                    this.setFieldLayout('grid');
                }
                break;
            case 'C':
                if (event.shiftKey) {
                    this.setFieldLayout('circle');
                }
                break;

            case 'Y':
                if (event.shiftKey) {
                    this.setFieldLayout('hex');
                }
                break;

            case 'M':
                if (event.shiftKey) {
                    // Debug material loading
                    //Logger.Instance.Info('🔍 Manual material loading debug requested', 'Debug');
                    this.testDebugManager.debugMaterialLoading();
                }
                break;
            case 'X':
                if (event.shiftKey && event.ctrlKey) {
                    // Force complete material loading
                    Logger.Instance.Warn('🚫 Manual force complete material loading', 'Debug');
                    this.materialLoadingManager.forceComplete();
                }
                break;
            case 'O':
                if (event.shiftKey) {
                    // Toggle Camera Orientation Gizmo
                    this.toggleGizmo();
                }
                break;
            case 'D':
                if (event.shiftKey) {
                    // Debug Gizmo
                    this.testDebugManager.debugGizmo();
                }
                break;
            case 'U':
                if (event.shiftKey) {
                    // Debug Camera Target and Transform
                    this.testDebugManager.debugCameraTarget();
                }
                break;
            case 't':
                // SceneNode Transform Hierarchy Test (Toggle)
                if (this.sceneNodeTransformTest) {
                    this.sceneNodeTransformTest.toggle();
                } else {
                    console.log('⚠️ SceneNode Transform Test not available yet');
                }
                break;
        }
    };

    private setupKeyHandlers(): void {
        if (!this.canvas) return;
        this.canvas.setAttribute('tabindex', '0');
        this.canvas.style.outline = 'none';
        this.canvas.focus();
        this.canvas.addEventListener('keydown', this.handleKeyDown);
    }

    private setupMouseHandlers(): void {
        // ⚠️ REMOVED DUPLICATE MOUSE HANDLERS
        // The InputManager system already handles click events via handleInputClick() and handleInputDoubleClick()
        // Having both systems active was causing double event processing and potential false positives
        
        // First, ensure no old listeners are active
        this.cleanupMouseHandlers();
        
        // NOTE: Mouse click handling is now exclusively done through the InputManager system
        // via setupInputManagerEventHandlers() -> handleInputClick() and handleInputDoubleClick()
        // This prevents duplicate event processing that could cause false positive POI selections
        
        //Logger.Instance.Debug('🖱️ Mouse handlers setup completed (using InputManager system only)', 'InputManager');
    }

    private cleanupKeyHandlers(): void {
        if (!this.canvas) return;
        this.canvas.removeEventListener('keydown', this.handleKeyDown);
    }

    private cleanupMouseHandlers(): void {
        if (this.unsubscribeDblClick) {
            this.unsubscribeDblClick();
            this.unsubscribeDblClick = null;
        }
        if (this.unsubscribeClick) {
            this.unsubscribeClick();
            this.unsubscribeClick = null;
        }
    }
    
    public async loadContent(content: any, filePath?: string): Promise<void> {
        this.addError("Loading not implemented in demo app");
        return Promise.resolve();
    }

    /**
     * Load a presentation from markdown content
     */
    public async loadPresentationFromMarkdown(markdownContent: string, filename?: string): Promise<void> {
        try {
            // Debug trace to identify double-load sources
            console.groupCollapsed('[Engine:load] loadPresentationFromMarkdown');
            console.info('file=', filename || 'untitled', 'len=', (markdownContent || '').length);
            // Only show trace in dev to avoid noisy logs in prod
            if (typeof process === 'undefined' || process.env?.NODE_ENV !== 'production') {
                try { console.trace(); } catch {}
            }
            console.groupEnd();
        } catch {}
        
        try {
            // Clear existing POIs
            this.clearAllPOIs();
            
            // Parse the markdown content
            const parser = new MarkdownParser();
            const poiDataInstances = await parser.parse(markdownContent, this.sceneSettings, this.appSettings, this.layoutData);

            // Ladebildschirm an die Praesentation angleichen: die Globals sind ab
            // hier bekannt, der Grossteil der Ladezeit (Texturen) kommt erst noch.
            // Vorher zeigt der Loader das Brand-Rot als Fallback (Wunsch Alex:
            // Loader farblich zur Praesentation, #119661-Folge).
            try {
                const c1 = (this.sceneSettings as any)?.backgroundGradientColor1?.value?.toString?.();
                const c2 = (this.sceneSettings as any)?.backgroundGradientColor2?.value?.toString?.();
                const angle = (this.sceneSettings as any)?.backgroundGradientAngle?.value ?? 180;
                if (c1 && c2) this.loadingManager.setBackgroundGradient(angle, c1, c2);
            } catch {}
            // // Force label render mode to a non-html2canvas path in production builds
            // try {
            //     poiDataInstances.forEach((d: any) => {
            //         if (d && d.labelRenderMode && d.labelRenderMode.value !== 'screen-html-static') {
            //             d.labelRenderMode.value = 'screen-html-static';
            //         }
            //     });
            // } catch {}

            // Ensure layout system is initialized before adding POIs
            if (!this.layout) {
                this.setupLayoutSystem();
            }

            // Apply layouter from parsed global header
            const parsedLayouter = this.layoutData.layouter;
            if (parsedLayouter && typeof parsedLayouter === 'string') {
                this.layout?.setLayoutData(this.layoutData);
            }

            // Create POIs from parsed data
            const pois = poiDataInstances.map((poiDataInstance: POIData) => {
                const name = this.extractTitleFromPOIData(poiDataInstance);
                return new POI(name, poiDataInstance, this.sceneSettings, this.appSettings, this.layoutData);
            });

            // Add POIs to scene in bulk — suppress per-POI layout passes.
            // The explicit applyLayoutWithProcessCurveUpdate() below runs the layout
            // exactly once after all POIs are registered.
            this._isBulkLoading = true;
            try {
                pois.forEach((poi: POI) => this.addPOI(poi));
            } finally {
                this._isBulkLoading = false;
            }

            // Set first POI as active
            if (this.pois.length > 0) {
                this.setActivePOI(this.pois[0]);
            }

            // Force layout application — apply immediately and also deferred
            // to handle shapes that create their Three.js meshes asynchronously
            if (this.layout && !this.layoutData.fieldAwarenessEnabled.value && this.pois.length > 0) {
                this.resetAllPOITransforms();
                this.applyLayoutWithProcessCurveUpdate();

                // Deferred re-application: shapes may not have their meshes yet.
                // Only re-apply if something marked the layout dirty (e.g. async mesh ready,
                // texture aspect changed). Idempotent calls skip cheaply.
                const reapply = () => {
                    if (this._layoutDirty && this.layout && this.pois.length > 0) {
                        this.applyLayoutWithProcessCurveUpdate();
                    }
                };
                setTimeout(reapply, 100);
                setTimeout(reapply, 500);
                setTimeout(reapply, 1500);
            }
            
        } catch (error) {
            Logger.Instance.Error(`❌ Failed to load presentation: ${error instanceof Error ? error.message : String(error)}`, "Presentation");
            throw error;
        }
    }

    /**
     * Import a presentation directly from ARTrack (no markdown) and inject POIs into the scene
     */
    public async importTrackFromSdk(trackId: number, options?: { useUTM?: boolean; utmZone?: string; altitudeScale?: number; trackScale?: number }): Promise<void> {
        if (!this.scene) return;
        const projection = options?.useUTM ? new UTMProjection({ utmZone: options?.utmZone, altitudeScale: options?.altitudeScale, trackScale: options?.trackScale }) : undefined;
        const factory = new TracksFactory(this.sceneSettings, this.appSettings);
        const { pois } = await factory.createFromTrack(trackId, { projection });
        // Clear and inject — suppress per-POI layout during bulk add
        this.clearAllPOIs();
        this._isBulkLoading = true;
        try {
            pois.forEach(p => this.addPOI(p));
        } finally {
            this._isBulkLoading = false;
        }
        if (this.pois.length > 0) {
            this.setActivePOI(this.pois[0]);
        }
        // Apply current layouter if field awareness disabled
        if (this.layout && !this.layoutData.fieldAwarenessEnabled.value) {
            this.applyLayoutWithProcessCurveUpdate();
        }
    }
    
    public async exportContent(): Promise<any> {
        console.log("EngineDemoApp does not support exporting content.");
        return Promise.resolve({});
    }
    
    protected override onStop(): void {
        super.onStop();
        console.log("EngineDemoApp stopped.");
    }

    // MOVED TO DebugManager.ts: lines 2172-2181

    // MOVED TO DebugManager.ts: lines 2183-2192

    // MOVED TO DebugManager.ts: lines 2194-2196

    // MOVED TO DebugManager.ts: lines 2198-2207

    // MOVED TO DebugManager.ts: lines 2209-2218

    // MOVED TO DebugManager.ts: lines 2220-2229

    // MOVED TO DebugManager.ts: lines 2231-2239

    // MOVED TO DebugManager.ts: lines 2241-2307
    

    private createStatsOverlay(): void {
        if (!this.cssContainer) return;

        this._statsOverlay = document.createElement('div');
        this._statsOverlay.style.position = 'absolute';
        this._statsOverlay.style.top = '10px';
        this._statsOverlay.style.left = '10px';
        this._statsOverlay.style.padding = '5px 8px';
        this._statsOverlay.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
        this._statsOverlay.style.color = 'white';
        this._statsOverlay.style.fontFamily = 'monospace';
        this._statsOverlay.style.fontSize = '12px';
        this._statsOverlay.style.zIndex = '1000';
        this._statsOverlay.style.lineHeight = '1.4';
        this._statsOverlay.style.minWidth = '300px';
        this._statsOverlay.innerHTML = 'Stats: init...';

        this.cssContainer.appendChild(this._statsOverlay);
    }

    private removeStatsOverlay(): void {
        if (this._statsOverlay && this._statsOverlay.parentNode) {
            this._statsOverlay.parentNode.removeChild(this._statsOverlay);
            this._statsOverlay = null;
        }
    }

    protected setupLayoutSystem(): void {
        // Create layout manager with custom POI transform application
        this.layout = new Layout('Demo Layout', this.layoutData, {
            preserveExplicitPositions: false,
            animateTransitions: true,
            transitionDuration: 1000
        });
        
        // Connect scene for debug visualization
        if (this.scene) {
            this.layout.setScene(this.scene.getThreeScene());

            // Initialize POI connection tube system
            if (!this._poiConnectionSystem) {
                this._poiConnectionSystem = new POIConnectionSystem(this.scene.getThreeScene());
            }
        }

        // ✅ Apply layout to POIs that were loaded before layout system was ready (only if field awareness is disabled)
        if (this.pois.length > 0) {
            if (this.layoutData.fieldAwarenessEnabled.value) {
                //Logger.Instance.Info(`🌪️ Skipping initial traditional layout - Field Awareness is enabled for ${this.pois.length} POIs`, 'Layout');
            } else {
                //Logger.Instance.Info(`🎯 Applying initial traditional layout to ${this.pois.length} POIs loaded from markdown`, 'Layout');
                this.applyLayoutWithProcessCurveUpdate();
            }
        }
        
        //Logger.Instance.Info('🎯 Layout system initialized with grid layouter', 'Layout');
        //Logger.Instance.Info('🚀 Modern Layouters available! Press "m" to test TestField layouters', 'Layout');
        //Logger.Instance.Debug('📋 Layout Testing Shortcuts:', 'Layout');
        //Logger.Instance.Debug('   1-3: Create 3/5/8 test POIs (replaces existing)', 'Layout');
        //Logger.Instance.Debug('   4: Create 12 test POIs OR add 12 more (performance testing)', 'Layout');
        //Logger.Instance.Debug('   5: Create 20 hexagon POIs for honeycomb layouts 🐝', 'Layout');
        //Logger.Instance.Debug('   6: Create 100 massive POIs for performance testing 🚀', 'Layout');
        //Logger.Instance.Debug('   g: Grid layout | c: Circle layout | s: Spiral layout', 'Layout');
        //Logger.Instance.Debug('   n: Line layout | r: Horizontal layout | v: Vertical layout | u: Uphill layout', 'Layout');
        //Logger.Instance.Debug('   x: Hexagonal layout (2D honeycomb) | o: Spherical hexagonal layout (3D sphere)', 'Layout');
        //Logger.Instance.Debug('   b: Toggle bounds visualization | f: Toggle frustum culling', 'Layout');
        //Logger.Instance.Debug('   p: Toggle Process Curve | P: Cycle Displacement Mode', 'Layout');
        //Logger.Instance.Debug('   F1: Toggle Settings Panel', 'Layout');
    }

    private setupfadeLabelOnActive(): void {
        // The fade labels on active functionality is now handled automatically
        // by each label's _updateOpacity method in their onUpdate loop.
        // No manual event handling needed since it's pull-based.
        //Logger.Instance.Info('🎭 Fade labels on active system initialized (pull-based)', 'Layout');
    }
    
    private setupLayoutPropertyHandlers(): void {
        // Layout system is managed by LayoutData, not AppSettings
        //Logger.Instance.Info('🔗 Layout property handlers managed by LayoutData directly', 'Layout');
        //Logger.Instance.Debug(`🏭 Available layouters from factory: ${LayouterFactory.getAvailableLayouters().join(', ')}`, 'Layout');

        // Connect individual layout property changes to trigger relayouts with debouncing
        this.setupLayoutPropertyRelayoutHandlers();
    }

    private setupLayoutPropertyRelayoutHandlers(): void {
        if (!this.layoutData) return;
        
        // 🎯 DIRECT RELAYOUT: No debounce - consistent with interpolated properties architecture
        const triggerImmediateRelayout = () => {
            this.applyLayoutToPOIs();
        };
        
        // ✅ LAYOUTER TYPE: Should trigger immediate relayout when layouter changes
        // BUT we need to ensure the Layout class is updated first
        this.layoutData.layouterProperty.on(() => {
            //console.log(`🔄 LAYOUTER PROPERTY CHANGED: New layouter = '${this.layoutData.layouter}'`);
            
            // CRITICAL: Update the Layout class's internal layouter BEFORE applying
            this.layout?.setLayoutData(this.layoutData);
            //console.log(`🔄 LAYOUTER PROPERTY: Updated Layout class with new layouter`);
            
            // Now apply the layout
            triggerImmediateRelayout();
        });
        
        // ✅ ALIGNMENT MODE: Should trigger immediate relayout for visual changes
        this.layoutData.alignmentMode.on((newMode, oldMode) => {
            //console.log(`🎯 ALIGNMENT MODE CHANGED: ${oldMode} → ${newMode}`);
            //console.log(`🎯 ALIGNMENT MODE: This should only affect orientation, not position`);
            
            // Log current POI positions BEFORE relayout
            if (this.pois.length > 0) {
                //console.log(`🎯 ALIGNMENT MODE: POI positions BEFORE relayout:`);
                this.pois.slice(0, 3).forEach((poi, index) => {
                    const pos = poi.poidata.position.value;
                    //console.log(`  POI ${index}: ${pos?.x.toFixed(2)}, ${pos?.y.toFixed(2)}, ${pos?.z.toFixed(2)}`);
                });
            }
            
            triggerImmediateRelayout();
            
            // Log current POI positions AFTER relayout
            setTimeout(() => {
                if (this.pois.length > 0) {
                    //console.log(`🎯 ALIGNMENT MODE: POI positions AFTER relayout:`);
                    this.pois.slice(0, 3).forEach((poi, index) => {
                        const pos = poi.poidata.position.value;
                        //console.log(`  POI ${index}: ${pos?.x.toFixed(2)}, ${pos?.y.toFixed(2)}, ${pos?.z.toFixed(2)}`);
                    });
                }
            }, 100);
        });
        
        // ✅ GRID LAYOUT PROPERTIES: Should trigger relayout when grid settings change
        this.layoutData.gridFixedSpacing?.on(() => triggerImmediateRelayout());
        this.layoutData.gridRows?.on(() => triggerImmediateRelayout());
        this.layoutData.gridCols?.on(() => triggerImmediateRelayout());
        this.layoutData.gridOffsetX?.on(() => triggerImmediateRelayout());
        this.layoutData.gridOffsetY?.on(() => triggerImmediateRelayout());
        this.layoutData.gridOffsetZ?.on(() => triggerImmediateRelayout());
        
        // ✅ CIRCLE LAYOUT PROPERTIES: Should trigger relayout when circle settings change
        this.layoutData.circleFixedSpacing?.on(() => triggerImmediateRelayout());
        this.layoutData.circleRadius?.on(() => triggerImmediateRelayout());
        this.layoutData.circleOffsetY?.on(() => triggerImmediateRelayout());
        
        // ✅ SPIRAL LAYOUT PROPERTIES: Should trigger relayout when spiral settings change
        this.layoutData.spiralFixedSpacing?.on(() => triggerImmediateRelayout());
        this.layoutData.spiralRadius?.on(() => triggerImmediateRelayout());
        this.layoutData.spiralHeight?.on(() => triggerImmediateRelayout());
        this.layoutData.spiralTurns?.on(() => triggerImmediateRelayout());
        this.layoutData.spiralAxis?.on(() => triggerImmediateRelayout());
        this.layoutData.spiralCenterLayout?.on(() => triggerImmediateRelayout());
        
        // ✅ LINE LAYOUT PROPERTIES: Should trigger relayout when line settings change
        this.layoutData.lineFixedSpacing?.on(() => triggerImmediateRelayout());
        this.layoutData.lineDirection?.on(() => triggerImmediateRelayout());
        this.layoutData.lineCenterLayout?.on(() => triggerImmediateRelayout());
        
        // ✅ HEXAGONAL LAYOUT PROPERTIES: Should trigger relayout when hex settings change
        this.layoutData.hexFixedSpacing?.on(() => triggerImmediateRelayout());
        this.layoutData.hexOffsetZ?.on(() => triggerImmediateRelayout());
        
        // ✅ SPHERICAL LAYOUT PROPERTIES: Should trigger relayout when sphere settings change
        this.layoutData.sphereFixedSpacing?.on(() => triggerImmediateRelayout());
        this.layoutData.sphereRadius?.on(() => triggerImmediateRelayout());
        this.layoutData.sphereSubdivisions?.on(() => triggerImmediateRelayout());
        
        // ✅ GLOBAL ROTATION: Should trigger relayout when rotation changes
        this.layoutData.rotationStep?.on(() => triggerImmediateRelayout());
        
        // ✅ GLOBAL ORIENTATION: Should trigger relayout when orientation settings change
        this.layoutData.upVectorMode?.on(() => triggerImmediateRelayout());
        
        // ✅ SPACING MODE: Should trigger relayout when spacing mode changes (Fixed vs Distribute)
        this.layoutData.spacingMode?.on(() => triggerImmediateRelayout());
        
        // ✅ MODERN LAYOUTER PROPERTIES: Should trigger relayout when modern layouter settings change
        // Modern 1D Layouters
        this.layoutData.modernLineFixedSpacing?.on(() => triggerImmediateRelayout());
        this.layoutData.modernCircleFixedSpacing?.on(() => triggerImmediateRelayout());
                    this.layoutData.modernCircleRadius?.on(() => triggerImmediateRelayout()); // 🔄 Unified radius for all modern layouters
        this.layoutData.modernSpiralFixedSpacing?.on(() => triggerImmediateRelayout());
        this.layoutData.modernSpiralGrowth?.on(() => triggerImmediateRelayout());
        this.layoutData.modernSpiralStartDistance?.on(() => triggerImmediateRelayout());
        this.layoutData.modernSpiralTurns?.on(() => triggerImmediateRelayout());
        this.layoutData.modernSpiralMaxRadius?.on(() => triggerImmediateRelayout());
        
        // Modern 2D/3D Layouters
        this.layoutData.modernSphereGridFixedSpacing?.on(() => triggerImmediateRelayout());
        this.layoutData.modernPlaneGridFixedSpacing?.on(() => triggerImmediateRelayout());
        this.layoutData.modernSphereHexFixedSpacing?.on(() => triggerImmediateRelayout());
        this.layoutData.modernSphereHexRingsFixedSpacing?.on(() => triggerImmediateRelayout());
        this.layoutData.modernPlaneHexRingsFixedSpacing?.on(() => triggerImmediateRelayout());
        this.layoutData.modernSphereHexHoneycombFixedSpacing?.on(() => triggerImmediateRelayout());
        
        // Modern Global Settings
        this.layoutData.modernFlip180?.on(() => triggerImmediateRelayout());
        
        //Logger.Instance.Info('🔗 Connected ALL layout properties (traditional + modern) to immediate relayout system', 'Layout');
    }
    
    private applyLayoutToPOIs(): void {
        if (!this.layout) return;
        
        // ⚠️ CONDITIONAL LAYOUT: Only apply traditional layout when field awareness is DISABLED
        if (this.layoutData.fieldAwarenessEnabled.value) {
            //Logger.Instance.Info(`🌪️ Skipping traditional layout - Field Awareness is enabled`, 'Layout');
            return;
        }
        
        // ✅ Apply traditional layout only when field awareness is disabled
        this.applyLayoutWithProcessCurveUpdate();
        
        //Logger.Instance.Info(`🎯 Traditional layout applied to ${this.pois.length} POIs using ${this.layoutData.layouter} layouter`, 'Layout');
    }
    
    /**
     * Apply layout and update process curve with proper timing
     */
    protected applyLayoutWithProcessCurveUpdate(): void {
        if (this.layout && this.pois.length > 0) {
            const layouterName = this.layoutData.layouter;
            this.layout.applyLayout(this.pois);
            // Layout consumed — mark clean. Subsequent dirty triggers (mesh ready, etc.) re-set it.
            this._layoutDirty = false;
            // getWorldPosition() triggers updateWorldTransform() — needed to read fresh values.
            if (this.pois.length >= 2) {
                const wp = this.pois[1].transform.getWorldPosition();
                Logger.Instance.Info(
                    `applyLayout(${layouterName}) on ${this.pois.length} POIs — pois[1].worldPos=(${wp.x.toFixed(2)},${wp.y.toFixed(2)},${wp.z.toFixed(2)})`,
                    'Layout'
                );
            }
        } else if (this.layout) {
            // Expected during a load: MarkdownParser applies GLOBAL layout keys to layoutData
            // BEFORE the POIs exist, and every layout property fires triggerImmediateRelayout()
            // (#1760). Nothing to lay out yet; the explicit applyLayout after POI creation runs
            // it, and the dirty flag covers deferred reapplies.
            this._layoutDirty = true;
            Logger.Instance.Info('applyLayout deferred — no POIs yet (layout property applied before content)', 'Layout');
        } else {
            Logger.Instance.Warn(`applyLayout skipped — layout system missing (poiCount=${this.pois.length})`, 'Layout');
        }
    }

    /**
     * Mark layout as dirty — call this when a POI's mesh becomes ready, texture aspect changes,
     * or any other event that could affect layout-relevant geometry. Deferred reapplies skip
     * the call if nothing is dirty.
     */
    public markLayoutDirty(): void {
        this._layoutDirty = true;
    }

    // Public methods for UI interaction
    public setLayouter(layouterName: string): void {
        //console.log(`🎯 SETLAYOUTER DEBUG: Attempting to set layouter to '${layouterName}'`);
        
        if (!LayouterFactory.isAvailable(layouterName)) {
            console.error(`❌ SETLAYOUTER ERROR: Layouter '${layouterName}' is not available`);
            //console.log(`Available layouters: ${LayouterFactory.getAvailableLayouters().join(', ')}`);
            Logger.Instance.Warn(`Layouter '${layouterName}' is not available`, 'Layout');
            return;
        }
        
        //console.log(`✅ SETLAYOUTER: Layouter '${layouterName}' is available`);
        
        // IMPORTANT: Reset transforms when switching layouters to prevent artifacts
        this.resetAllPOITransforms();
        //console.log(`🔄 SETLAYOUTER: Reset all POI transforms`);
        
        // Store old layouter for comparison
        const oldLayouter = this.layoutData.layouter;
        //console.log(`🔄 SETLAYOUTER: Old layouter was '${oldLayouter}'`);
        
        this.layoutData.layouter = layouterName;
        //console.log(`🔄 SETLAYOUTER: Set layoutData.layouter to '${layouterName}'`);
        //console.log(`🔄 SETLAYOUTER: layoutData.layouter now reads as '${this.layoutData.layouter}'`);
        
        this.layout?.setLayoutData(this.layoutData);
        //console.log(`🔄 SETLAYOUTER: Called layout.setLayoutData()`);
        
        // ✅ Apply new layout to existing POIs (only if field awareness is disabled)
        if (this.layout && this.pois.length > 0) {
            //console.log(`🔄 SETLAYOUTER: Have ${this.pois.length} POIs to layout`);
            //console.log(`🔄 SETLAYOUTER: Field awareness enabled: ${this.layoutData.fieldAwarenessEnabled.value}`);
            
            if (this.layoutData.fieldAwarenessEnabled.value) {
                //console.log(`🌪️ SETLAYOUTER: Skipping layouter change - Field Awareness is enabled`);
                //Logger.Instance.Info(`🌪️ Skipping layouter change - Field Awareness is enabled`, 'Layout');
            } else {
                //console.log(`🎯 SETLAYOUTER: Applying layout with '${layouterName}' layouter`);
                
                // 🐛 DEBUG: Log all POI scales BEFORE layout application
                this.pois.forEach((poi, index) => {
                    const currentScale = poi.poidata.scale.value ?? 1.0;
                    //console.log(`🔍 PRE-LAYOUT: POI ${index} (${poi.name}) scale: ${currentScale.toFixed(4)}`);
                });
                
                this.applyLayoutWithProcessCurveUpdate();
                //console.log(`✅ SETLAYOUTER: Layout applied successfully`);
                
                // 🐛 DEBUG: Log all POI scales AFTER layout application
                this.pois.forEach((poi, index) => {
                    const currentScale = poi.poidata.scale.value ?? 1.0;
                    //console.log(`🔍 POST-LAYOUT: POI ${index} (${poi.name}) scale: ${currentScale.toFixed(4)}`);
                });
            }
        } else {
            //console.log(`⚠️ SETLAYOUTER: Cannot apply layout - layout=${!!this.layout}, pois.length=${this.pois.length}`);
        }
        
        //console.log(`🎯 SETLAYOUTER COMPLETE: Switched to ${layouterName} layouter (transforms reset)`);
        //Logger.Instance.Info(`🎯 Switched to ${layouterName} layouter (transforms reset)`, 'Layout');
    }
    
    public getLayoutData(): LayoutData {
        return this.layoutData;
    }
    
    public getAvailableLayouters(): string[] {
        return LayouterFactory.getAvailableLayouters();
    }

    // 🚀 Test Modern Layouters from TestField
    public testModernLayouters(): void {
        //Logger.Instance.Info('🚀 Testing Modern Layouters...', 'Layout');
        
        // Create some test POIs if none exist
        if (this.pois.length === 0) {
            this.createTestPOIs(5);
        }
        
        // Test modern-line layouter
        //Logger.Instance.Info('🔧 Testing modern-line layouter', 'Layout');
        this.setLayouter('modern-line');
        this.layoutData.modernLineFixedSpacing.value = 3.0;
        this.layoutData.modernFlip180.value = false;
        
        // Apply after short delay to see the effect
        setTimeout(() => {
            //Logger.Instance.Info('🔄 Flipping objects 180 degrees', 'Layout');
            this.layoutData.modernFlip180.value = true;
        }, 2000);
        
        // Test modern-circle after another delay
        setTimeout(() => {
            //Logger.Instance.Info('🔄 Testing modern-circle layouter', 'Layout');
            this.setLayouter('modern-circle');
            this.layoutData.modernCircleRadius.value = 7.0; // 🔄 Unified radius
            this.layoutData.modernCircleFixedSpacing.value = 1.5;
        }, 4000);
        
        // Test modern-spiral after another delay
        setTimeout(() => {
            //Logger.Instance.Info('🌀 Testing modern-spiral layouter', 'Layout');
            this.setLayouter('modern-spiral');
            this.layoutData.modernSpiralGrowth.value = 3.0;
            this.layoutData.modernSpiralTurns.value = 3.0;
            this.layoutData.modernSpiralMaxRadius.value = 5.0;
        }, 6000);
        
        // Test adaptive layouters
        setTimeout(() => {
            //Logger.Instance.Info('🎯 Testing modern-line-adaptive layouter', 'Layout');
            this.setLayouter('modern-line-adaptive');
            this.layoutData.modernLineFixedSpacing.value = 1.0;
        }, 8000);
        
        setTimeout(() => {
            //Logger.Instance.Info('🎯 Testing modern-circle-adaptive layouter', 'Layout');
            this.setLayouter('modern-circle-adaptive');
            this.layoutData.modernCircleRadius.value = 8.0; // 🔄 Unified radius
        }, 10000);
        
        setTimeout(() => {
            //Logger.Instance.Info('🎯 Testing modern-spiral-adaptive layouter', 'Layout');
            this.setLayouter('modern-spiral-adaptive');
            this.layoutData.modernSpiralGrowth.value = 2.5;
            this.layoutData.modernSpiralStartDistance.value = 0.2;
        }, 12000);
        
        // Test new Surface Layouters (2D/3D)
        setTimeout(() => {
            //Logger.Instance.Info('🌐 Testing modern-sphere-grid layouter', 'Layout');
            this.setLayouter('modern-sphere-grid');
            this.layoutData.modernSphereGridFixedSpacing.value = 2.0;
        }, 14000);
        
        setTimeout(() => {
            //Logger.Instance.Info('📐 Testing modern-plane-grid layouter', 'Layout');
            this.setLayouter('modern-plane-grid');
            this.layoutData.modernPlaneGridFixedSpacing.value = 2.5;
        }, 16000);
        
        setTimeout(() => {
            //Logger.Instance.Info('🔸 Testing modern-sphere-hex layouter', 'Layout');
            this.setLayouter('modern-sphere-hex');
            this.layoutData.modernSphereHexFixedSpacing.value = 1.2;
        }, 18000);
        
        setTimeout(() => {
            //Logger.Instance.Info('🔸 Testing modern-sphere-hex-rings layouter', 'Layout');
            this.setLayouter('modern-sphere-hex-rings');
            this.layoutData.modernSphereHexRingsFixedSpacing.value = 1.5;
        }, 20000);
        
        setTimeout(() => {
            //Logger.Instance.Info('🔸 Testing modern-plane-hex-rings layouter', 'Layout');
            this.setLayouter('modern-plane-hex-rings');
            this.layoutData.modernPlaneHexRingsFixedSpacing.value = 2.0;
        }, 22000);
        
        setTimeout(() => {
            //Logger.Instance.Info('🔸 Testing modern-sphere-hex-honeycomb layouter', 'Layout');
            this.setLayouter('modern-sphere-hex-honeycomb');
            this.layoutData.modernSphereHexHoneycombFixedSpacing.value = 1.0;
        }, 24000);
        
        setTimeout(() => {
            //Logger.Instance.Info('✅ ALL 12 Modern Layouters test sequence complete!', 'Layout');
            //Logger.Instance.Info('🎉 TestField JavaScript integration fully operational!', 'Layout');
        }, 26000);
    }

    public createTestPOIs(count: number = 20): void {
        //console.log(`Creating ${count} test POIs for orientation debugging...`);
        
        // Clear existing POIs first
        this.clearAllPOIs();
        this.testPOIBatchCounter = 1;
        this.hasTestPOIs = true;
        
        const colors = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#ffa500', '#800080', '#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#6c5ce7', '#a29bfe', '#fd79a8', '#fdcb6e', '#e17055', '#00b894', '#e84393', '#0984e3'];
        
        for (let i = 0; i < count; i++) {
            const poidata = new POIData(this.sceneSettings, this.appSettings);
            poidata.shape.value = "hexagon";
            poidata.markdown.value = `## Hex ${i + 1}\n\n**Orientation Test**`;
            poidata.showLabel.value = true;
            poidata.scale.value = 0.8;
            poidata.depth.value = 0.1;
            const poi = new POI(`TestHex_${i}`, poidata, this.sceneSettings, this.appSettings, this.layoutData);
            this.addPOI(poi);
        }
        
        // Set camera to look at first POI
        if (this.pois.length > 0) {
            this.setActivePOI(this.pois[0]);
        }
        
        //console.log(`Created ${count} test POIs with HEXAGONS (shallow depth) for orientation debugging.`);
        //console.log(`🔥 HEXAGONS: Shallow depth for clear orientation visibility`);
        //console.log(`🔥 Use F1 settings to test ALIGNED vs PERPENDICULAR orientations!`);
    }

    public addMoreTestPOIs(count: number = 12): void {
        const currentCount = this.pois.length;
        this.testPOIBatchCounter++;
        
        //console.log(`🔥 Performance Test: Adding ${count} more test POIs (Batch ${this.testPOIBatchCounter}) - Total will be ${currentCount + count}`);
        
        const shapes = ['box', 'sphere', 'cylinder', 'torus', 'triangle'];
        const colors = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#ffa500', '#800080', '#ff8000', '#8000ff', '#00ff80', '#8080ff'];
        
        for (let i = 0; i < count; i++) {
            const globalIndex = currentCount + i;
            const poidata = new POIData(this.sceneSettings, this.appSettings);
            poidata.shape.value = shapes[i % shapes.length];
            poidata.markdown.value = `POI ${globalIndex + 1}`;
            poidata.showLabel.value = true;
            poidata.scale.value = 0.8;
            poidata.color.value = new Color(colors[i % colors.length]);
            
            const poi = new POI(`TestPOI_B${this.testPOIBatchCounter}_${i}`, poidata, this.sceneSettings, this.appSettings, this.layoutData);
            this.addPOI(poi);
        }
        
        // Reapply layout to all POIs (including new ones)
        this.applyLayoutToPOIs();
        
        //console.log(`🚀 Performance Test: Now have ${this.pois.length} total POIs (added ${count} in batch ${this.testPOIBatchCounter})`);
        //console.log(`💡 Keep pressing "4" to add more POIs and test performance limits!`);
    }

    public createHexagonTestPOIs(count: number = 20): void {
        //console.log(`🐝 Creating ${count} hexagon test POIs for honeycomb layout demonstration...`);
        
        // Clear existing POIs first
        this.clearAllPOIs();
        this.testPOIBatchCounter = 1;
        this.hasTestPOIs = true;
        
        const colors = ['#ffb300', '#ff8f00', '#ff6f00', '#e65100', '#bf360c', '#8d4e00', '#6a3e00', '#4a2c00'];
        
        for (let i = 0; i < count; i++) {
            const poidata = new POIData(this.sceneSettings, this.appSettings);
            poidata.shape.value = "hexagon";
            poidata.markdown.value = `🐝 ${i + 1}`;
            poidata.color.value = new Color(colors[i % colors.length]);
            poidata.scale.value = 1;
            poidata.uri.value = ['assets/Tarot/0 chariot.mp4'];
            poidata.cornerRadius.value = 0.05;
            
            const poi = new POI(`HexagonPOI_${i}`, poidata, this.sceneSettings, this.appSettings, this.layoutData);
            this.addPOI(poi);
        }
        
        // Switch to hexagonal layout automatically
        this.setLayouter('hexagonal');
        
        // Set camera to look at first POI
        if (this.pois.length > 0) {
            this.setActivePOI(this.pois[0]);
        }
        
        //console.log(`🐝 Created ${count} hexagon POIs with honeycomb layout. Press 'x' for 2D honeycomb, 'o' for 3D sphere!`);
    }

    public createMassivePOITest(count: number = 100): void {
        //console.log(`🐝 Creating ${count} massive hexagon test POIs for performance demonstration...`);
        
        // Clear existing POIs first
        this.clearAllPOIs();
        this.testPOIBatchCounter = 1;
        this.hasTestPOIs = true;
        
        const colors = [
            '#ffb300', '#ff8f00', '#ff6f00', '#e65100', '#bf360c', '#8d4e00', '#6a3e00', '#4a2c00',
            '#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#ffa500', '#800080',
            '#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#6c5ce7', '#a29bfe', '#fd79a8', '#fdcb6e',
            '#e17055', '#00b894', '#e84393', '#0984e3', '#74b9ff', '#00cec9', '#55a3ff', '#fd79a8'
        ];
        
        for (let i = 0; i < count; i++) {
            const poidata = new POIData(this.sceneSettings, this.appSettings);
            poidata.shape.value = "hexagon";
            poidata.markdown.value = `🐝 ${i + 1}`;
            poidata.showLabel.value = true;
            poidata.color.value = new Color(colors[i % colors.length]);
            
            const poi = new POI(`MassiveHexagonPOI_${i}`, poidata, this.sceneSettings, this.appSettings, this.layoutData);
            this.addPOI(poi);
        }
        
        // Switch to hexagonal layout for better organization (like the "5" key does)
        this.setLayouter('hexagonal');
        
        // Set camera to look at first POI
        if (this.pois.length > 0) {
            this.setActivePOI(this.pois[0]);
        }
        
        //console.log(`🐝 Created ${count} massive hexagon POIs with hexagonal layout. This is a great test for our new label scaling system!`);
    }
    

    private handleOrbitInput(): void {
        const inputManager = InputEventManager.Instance;
        
        // Check for mouse or touch input with movement
        const isMouseOrTouchDragging = 
            (inputManager.isMouseDown && (inputManager.mouseDelta.x !== 0 || inputManager.mouseDelta.y !== 0)) ||
            (inputManager.isTouchDown && (inputManager.touchDelta.x !== 0 || inputManager.touchDelta.y !== 0));
            
        if (isMouseOrTouchDragging) {
            if (!this.appSettings?.cameraLatitude || !this.appSettings?.cameraLongitude) {
                Logger.Instance.Warn(`⚠️ Cannot orbit: camera lat/lon settings not available`, 'Input');
                return;
            }
            
            // Use touch deltas if touching, otherwise use mouse deltas
            const deltaX = inputManager.isTouchDown ? inputManager.touchDelta.x : inputManager.mouseDelta.x;
            const deltaY = inputManager.isTouchDown ? inputManager.touchDelta.y : inputManager.mouseDelta.y;
            
            // Only process if there's actual movement
            if (Math.abs(deltaX) > 0.1 || Math.abs(deltaY) > 0.1) {
                // Current camera angles
                const currentLongitude = this.appSettings.cameraLongitude.value ?? 0;
                const currentLatitude = this.appSettings.cameraLatitude.value ?? -16;
                
                // Orbit sensitivity (degrees per pixel of drag) — touch needs higher sensitivity
                const baseOrbitSensitivity = 0.5;
                const orbitSensitivity = DeviceInfo.isMobile ? baseOrbitSensitivity * 1.5 : baseOrbitSensitivity;
                
                // Calculate new angles
                // Horizontal mouse movement (deltaX) controls longitude (left/right orbit)
                // Vertical mouse movement (deltaY) controls latitude (up/down orbit)
                const newLongitude = currentLongitude - (deltaX * orbitSensitivity); // Inverted X for intuitive orbit
                const newLatitude = currentLatitude + (deltaY * orbitSensitivity); // Normal Y direction for intuitive feel
                
                // Clamp to bounds
                const minLongitude = this.appSettings.cameraLongitude.boundsMin ?? -180;
                const maxLongitude = this.appSettings.cameraLongitude.boundsMax ?? 180;
                const minLatitude = this.appSettings.cameraLatitude.boundsMin ?? -180;
                const maxLatitude = this.appSettings.cameraLatitude.boundsMax ?? 180;
                
                const clampedLongitude = Math.max(minLongitude, Math.min(maxLongitude, newLongitude));
                const clampedLatitude = Math.max(minLatitude, Math.min(maxLatitude, newLatitude));
                
                // Only update if values actually changed
                let updated = false;
                if (Math.abs(clampedLongitude - currentLongitude) > 0.01) {
                    this.appSettings.cameraLongitude.value = clampedLongitude;
                    updated = true;
                }
                if (Math.abs(clampedLatitude - currentLatitude) > 0.01) {
                    this.appSettings.cameraLatitude.value = clampedLatitude;
                    updated = true;
                }
                
                if (updated) {
                    //Logger.Instance.Debug(`🔄 Orbit: mouse(${deltaX.toFixed(0)}, ${deltaY.toFixed(0)}) → lat=${clampedLatitude.toFixed(1)}°, lon=${clampedLongitude.toFixed(1)}°`, 'Input');
                }
            }
        }
    }

    /**
     * Set field layout type
     */
    public setFieldLayout(layoutType: 'line' | 'grid' | 'circle' | 'spiral' | 'hex' | 'hex-spherical' | 'grid-spherical'): void {
        if (!this.fieldContext) {
            Logger.Instance.Warn('🌪️ Field Context not available', 'Field');
            return;
        }
        
        // IMPORTANT: Reset all POI transforms before layout change to prevent artifacts
        this.resetAllPOITransforms();
        
        // Auto-enable field awareness when setting field layout
        if (!this.layoutData.fieldAwarenessEnabled.value) {
            //Logger.Instance.Info('🌪️ Auto-enabling Field Awareness for field layout', 'Field');
            this.enableFieldAwareness();
        }
        
        // Map to FieldLayoutType enum first
        let fieldLayoutType: FieldLayoutType;
        switch (layoutType) {
            case 'line': fieldLayoutType = FieldLayoutType.Line; break;
            case 'grid': fieldLayoutType = FieldLayoutType.Grid; break;
            case 'circle': fieldLayoutType = FieldLayoutType.Circle; break;
            case 'spiral': fieldLayoutType = FieldLayoutType.Spiral; break;
            case 'hex': fieldLayoutType = FieldLayoutType.Hex; break;
            case 'hex-spherical': fieldLayoutType = FieldLayoutType.HexSpherical; break;
            case 'grid-spherical': fieldLayoutType = FieldLayoutType.GridSpherical; break;
            default: 
                Logger.Instance.Warn(`🌪️ Unknown field layout type: ${layoutType}`, 'Field');
                return;
        }
        
        // Update UI property to sync
        this.layoutData.fieldLayoutType.value = fieldLayoutType;
        
        // Update field context layout type
        this.fieldContext.changeLayoutType(fieldLayoutType);
        
        // POIs self-manage field awareness - no need for manual POI management
        
        //Logger.Instance.Info(`🌪️ Field Layout set to: ${layoutType}`, 'Field');
        //console.log(`🌪️ FIELD LAYOUT: ${layoutType.toUpperCase()}`);
        //console.log(`🌪️ FIELD AWARENESS: ${this.layoutData.fieldAwarenessEnabled.value ? 'ENABLED' : 'DISABLED'}`);
        //console.log(`🌪️ POIs in field: ${this.pois.length}`);
        //console.log(`🔥 Switch between ALIGNED/PERPENDICULAR in F1 settings to see orientation changes!`);
        
        this.eventManager.emit('field:layout-changed', { layoutType });
    }
    
    /**
     * Reset all POI transforms to clean state (prevents artifacts when switching layouts)
     */
    private resetAllPOITransforms(): void {
        console.log('🔄 Resetting all POI transforms to prevent layout artifacts...');
        
        this.pois.forEach((poi, index) => {
            try {
                // Reset main POI transform
                if (poi.transform) {
                    poi.transform.scale.setImmediate(new Vector3(1, 1, 1)); // Reset scale
                    // Don't reset position/rotation as that will be set by new layout
                }
                
                // Reset shape transform if it exists
                if (poi.shape && poi.shape.transform) {
                    poi.shape.transform.position.setImmediate(new Vector3(0, 0, 0)); // Reset local offset
                    // Preserve user-defined scale from POIData
                    const userScale = (poi.poidata?.scale?.value ?? 1.0);
                    const targetScale = new Vector3(userScale, userScale, userScale);
                    poi.shape.transform.scale.setImmediate(targetScale);
                    // Don't reset rotation as that will be set by field awareness
                }
                
                // DON'T reset POI data scale - preserve original scale from markdown/POIData
                // The layouter will combine its scale with the original scale if needed
                // poi.poidata.scale should always preserve the user's intended scale from markdown
                
            } catch (error) {
                console.warn(`Failed to reset transforms for POI ${poi.id}:`, error);
            }
        });
        
        //console.log(`✅ Reset transforms for ${this.pois.length} POIs`);
    }

    // ==================== CAMERA ORIENTATION GIZMO ====================
    
    /**
     * Create Blender-style camera orientation gizmo in top-right corner
     */
    private createGizmo(): void {
        if (this._gizmoCanvas) return; // Already created
        
        // Create gizmo canvas - positioned below AR button and auth area
        this._gizmoCanvas = document.createElement('canvas');
        this._gizmoCanvas.width = 120;
        this._gizmoCanvas.height = 120;
        this._gizmoCanvas.style.position = 'absolute';
        this._gizmoCanvas.style.top = '120px'; // 🎯 Moved down to avoid AR button (at 20px) and auth area
        this._gizmoCanvas.style.right = '10px';
        this._gizmoCanvas.style.zIndex = '1000';
        this._gizmoCanvas.style.pointerEvents = 'none';
        this._gizmoCanvas.style.border = '2px solid rgba(255,255,255,0.5)';
        this._gizmoCanvas.style.borderRadius = '8px';
        this._gizmoCanvas.style.background = 'rgba(0,0,0,0.8)';
        
        // Add to document
        document.body.appendChild(this._gizmoCanvas);
        
        // Create camera info display under gizmo  
        this._gizmoInfoDisplay = document.createElement('div');
        this._gizmoInfoDisplay.style.cssText = `
            position: absolute;
            top: 250px;
            right: 10px;
            width: 120px;
            background: rgba(0, 0, 0, 0.8);
            color: #ffffff;
            font-family: 'Courier New', monospace;
            font-size: 10px;
            padding: 8px;
            border-radius: 6px;
            border: 1px solid rgba(255, 255, 255, 0.3);
            z-index: 1000;
            text-align: center;
            line-height: 1.3;
        `;
        this._gizmoInfoDisplay.innerHTML = 'Loading...';
        document.body.appendChild(this._gizmoInfoDisplay);
        
        // Create gizmo renderer
        this._gizmoRenderer = new THREE.WebGLRenderer({
            canvas: this._gizmoCanvas,
            alpha: true,
            antialias: true
        });
        this._gizmoRenderer.setSize(120, 120);
        this._gizmoRenderer.setClearColor(0x000000, 0);
        
        // Create gizmo scene
        this._gizmoScene = new THREE.Scene();
        
        // Create gizmo camera (orthographic for consistent size)
        this._gizmoCamera = new THREE.OrthographicCamera(-2, 2, 2, -2, 0.1, 10);
        this._gizmoCamera.position.set(2, 2, 2);
        this._gizmoCamera.lookAt(0, 0, 0);
        this._gizmoCamera.up.set(0, 1, 0);
        
        // Create axis helper group
        this._gizmoAxisHelper = new THREE.Group();
        
        // Create X-axis (Red)
        const xGroup = new THREE.Group();
        
        // X-axis line
        const xLineGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(1.5, 0, 0)
        ]);
        const xLineMaterial = new THREE.LineBasicMaterial({ color: 0xff0000, linewidth: 3 });
        const xLine = new THREE.Line(xLineGeometry, xLineMaterial);
        xGroup.add(xLine);
        
        // X-axis arrow
        const xArrowGeometry = new THREE.ConeGeometry(0.1, 0.3, 8);
        const xArrowMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
        const xArrow = new THREE.Mesh(xArrowGeometry, xArrowMaterial);
        xArrow.position.set(1.5, 0, 0);
        xArrow.rotateZ(-Math.PI / 2);
        xGroup.add(xArrow);
        
        this._gizmoAxisHelper.add(xGroup);
        
        // Create Y-axis (Green)
        const yGroup = new THREE.Group();
        
        // Y-axis line
        const yLineGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(0, 1.5, 0)
        ]);
        const yLineMaterial = new THREE.LineBasicMaterial({ color: 0x00ff00, linewidth: 3 });
        const yLine = new THREE.Line(yLineGeometry, yLineMaterial);
        yGroup.add(yLine);
        
        // Y-axis arrow
        const yArrowGeometry = new THREE.ConeGeometry(0.1, 0.3, 8);
        const yArrowMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
        const yArrow = new THREE.Mesh(yArrowGeometry, yArrowMaterial);
        yArrow.position.set(0, 1.5, 0);
        yGroup.add(yArrow);
        
        this._gizmoAxisHelper.add(yGroup);
        
        // Create Z-axis (Blue)
        const zGroup = new THREE.Group();
        
        // Z-axis line
        const zLineGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(0, 0, 1.5)
        ]);
        const zLineMaterial = new THREE.LineBasicMaterial({ color: 0x0000ff, linewidth: 3 });
        const zLine = new THREE.Line(zLineGeometry, zLineMaterial);
        zGroup.add(zLine);
        
        // Z-axis arrow
        const zArrowGeometry = new THREE.ConeGeometry(0.1, 0.3, 8);
        const zArrowMaterial = new THREE.MeshBasicMaterial({ color: 0x0000ff });
        const zArrow = new THREE.Mesh(zArrowGeometry, zArrowMaterial);
        zArrow.position.set(0, 0, 1.5);
        zArrow.rotateX(Math.PI / 2);
        zGroup.add(zArrow);
        
        this._gizmoAxisHelper.add(zGroup);
        
        // Add center sphere
        const centerGeometry = new THREE.SphereGeometry(0.08);
        const centerMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
        const centerSphere = new THREE.Mesh(centerGeometry, centerMaterial);
        this._gizmoAxisHelper.add(centerSphere);
        
        // Add ambient light for better visibility
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
        this._gizmoScene.add(ambientLight);
        
        // Add directional light
        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(1, 1, 1);
        this._gizmoScene.add(directionalLight);
        
        // Add to scene
        this._gizmoScene.add(this._gizmoAxisHelper);
        
        console.log('🧭 Camera orientation gizmo created (Blender-style)');
        console.log('🧭 Gizmo components created:', {
            scene: !!this._gizmoScene,
            camera: !!this._gizmoCamera,
            axisHelper: !!this._gizmoAxisHelper,
            renderer: !!this._gizmoRenderer,
            sceneChildren: this._gizmoScene.children.length
        });
        
        // Initial render
        this.updateGizmo();
        
        // Also add a simple stationary test to verify the gizmo works
        setTimeout(() => {
            console.log('🧭 Rendering stationary gizmo test...');
            if (this._gizmoRenderer && this._gizmoScene && this._gizmoCamera) {
                this._gizmoRenderer.render(this._gizmoScene, this._gizmoCamera);
                console.log('🧭 Stationary gizmo test complete');
                
                // Test background color
                this._gizmoRenderer.setClearColor(0x333333, 1);
                this._gizmoRenderer.render(this._gizmoScene, this._gizmoCamera);
                console.log('🧭 Background color test complete');
            }
        }, 100);
        

    }
    
    /**
     * Update gizmo to match main camera orientation
     */
    private updateGizmo(): void {
        if (!this._gizmoAxisHelper || !this._gizmoRenderer || !this._gizmoScene || !this._gizmoCamera) return;
        
        // Get main camera from scene - try multiple ways
        let mainCamera: THREE.Camera | null = null;
        
        if (this.scene) {
            // Try to find camera in scene
            mainCamera = this.scene.getThreeScene().children.find(obj => obj instanceof THREE.Camera) as THREE.Camera;
        }
        
        // If not found, try to get from camera manager
        if (!mainCamera && this.camera) {
            mainCamera = this.camera.internalCamera;
        }
        
        if (!mainCamera) {
            console.warn('🧭 No main camera found for gizmo update');
            return;
        }
        
        // Copy main camera rotation to gizmo
        this._gizmoAxisHelper.quaternion.copy(mainCamera.quaternion);
        
        // Update camera info display
        this.updateGizmoInfo();
        
        // Render gizmo
        this._gizmoRenderer.render(this._gizmoScene, this._gizmoCamera);
    }
    
    /**
     * Update camera info display under gizmo
     */
    private updateGizmoInfo(): void {
        if (!this._gizmoInfoDisplay || !this.appSettings) return;
        
        const distance = this.appSettings.cameraDistance?.value ?? 0;
        const latitude = this.appSettings.cameraLatitude?.value ?? 0;
        const longitude = this.appSettings.cameraLongitude?.value ?? 0;
        
        const html = `
            <div style="color: #00ff88; font-weight: bold; margin-bottom: 4px;">CAM INFO</div>
            <div>Dist: <span style="color: #ffff00;">${distance.toFixed(1)}</span></div>
            <div>Lat: <span style="color: #ff8888;">${latitude.toFixed(1)}°</span></div>
            <div>Lon: <span style="color: #88bbff;">${longitude.toFixed(1)}°</span></div>
        `;
        
        this._gizmoInfoDisplay.innerHTML = html;
    }
    
    /**
     * Remove gizmo from display
     */
    private removeGizmo(): void {
        if (this._gizmoCanvas) {
            document.body.removeChild(this._gizmoCanvas);
            this._gizmoCanvas = null;
        }
        
        if (this._gizmoInfoDisplay) {
            document.body.removeChild(this._gizmoInfoDisplay);
            this._gizmoInfoDisplay = null;
        }
        
        if (this._gizmoRenderer) {
            this._gizmoRenderer.dispose();
            this._gizmoRenderer = null;
        }
        
        this._gizmoScene = null;
        this._gizmoCamera = null;
        this._gizmoAxisHelper = null;
        
        console.log('🧭 Camera orientation gizmo removed');
    }
    
    /**
     * Toggle camera orientation gizmo visibility
     */
    public toggleGizmo(): void {
        if (this._gizmoCanvas) {
            this.removeGizmo();
            console.log('🧭 Camera orientation gizmo hidden');
        } else {
            this.createGizmo();
            console.log('🧭 Camera orientation gizmo shown');
        }
    }
    
    /**
     * Debug gizmo state
     */
    // MOVED TO DebugManager.ts: lines 3100-3135

    // MOVED TO DebugManager.ts: lines 3137-3190
     
    public toggleFieldDirectionAnimation(): void {
        if (this.isFieldDirectionAnimating) {
            // Stop animation
            if (this.fieldDirectionAnimation) {
                this.fieldDirectionAnimation.stop();
                this.fieldDirectionAnimation = null;
            }
            this.isFieldDirectionAnimating = false;
            //Logger.Instance.Info('🔄 Field Direction Animation STOPPED', 'Animation');
        } else {
            // Start animation
            if (!this.layoutData?.fieldDirection) {
                Logger.Instance.Warn('⚠️ Cannot start field direction animation: fieldDirection property not available', 'Animation');
                return;
            }
            
            // Create new bounds animation
            this.fieldDirectionAnimation = new BoundsAnimation<any>('Field Direction Animation');
            
            // Configure for field direction Y component animation
            this.fieldDirectionAnimation.setProperty(this.layoutData.fieldDirection);
            this.fieldDirectionAnimation.setAnimateComponent('y'); // Animate Y component for rotation
            this.fieldDirectionAnimation.setSpeed(0.5); // 0.5 cycles per second (slow rotation)
            this.fieldDirectionAnimation.setEasing('easeInOut'); // Smooth easing
            
            // Start animation
            this.fieldDirectionAnimation.start();
            this.isFieldDirectionAnimating = true;
            
            //Logger.Instance.Info('🔄 Field Direction Animation STARTED - Y component animation between bounds', 'Animation');
        }
    }

    /**
     * Toggle AR mode on/off
     */
    public async toggleAR(): Promise<void> {
        //Logger.Instance.Info('🥽 Toggle AR requested', 'AR');
        await this.arManager.toggleAR();
    }

    /**
     * 🍎 Trigger Apple QuickLook AR for the active POI (if it's a GLB model)
     */
    public triggerAppleQuickLookAR(): void {
        const activePOI = this.getCurrentPOI();
        
        if (!activePOI) {
            this.showARError('No active POI selected', 'Please navigate to a 3D model first');
            return;
        }

        // Check if active POI is a USDZ model (Apple QuickLook only supports USDZ, not GLB!)
        const poiUris = activePOI.poidata.uri?.value || [];
        const poiUri = poiUris[0]; // Use first URI for AR (main 3D model)
        
        if (!poiUri) {
            this.showARError('No 3D model found', `POI "${activePOI.name}" has no 3D model file`);
            return;
        }

        const isUSDZ = poiUri.toLowerCase().endsWith('.usdz');
        if (!isUSDZ) {
            this.showARError('USDZ required for AR', `Apple QuickLook requires USDZ format. "${activePOI.name}" uses ${poiUri.split('.').pop()?.toUpperCase()}`);
            return;
        }

        // Check if running on iOS Safari (Apple QuickLook support)
        const isIOSSafari = this.isIOSSafari();
        if (!isIOSSafari) {
            this.showARError('iOS Safari required', 'Apple QuickLook AR only works on iOS Safari');
            return;
        }

        //Logger.Instance.Info(`🍎 Triggering Apple QuickLook AR for POI "${activePOI.name}" (${poiUri})`, 'QuickLook');
        
        // Create Apple QuickLook AR link and trigger it
        this.createAndTriggerQuickLookLink(poiUri, activePOI.name);
    }

    /**
     * Check if running on iOS Safari
     */
    private isIOSSafari(): boolean {
        const userAgent = navigator.userAgent;
        const isIOS = /iPad|iPhone|iPod/.test(userAgent);
        const isSafari = /Safari/.test(userAgent) && !/Chrome|CriOS|FxiOS/.test(userAgent);
        return isIOS && isSafari;
    }

    /**
     * Create and trigger Apple QuickLook AR link
     */
    private createAndTriggerQuickLookLink(modelUri: string, poiName: string): void {
        // Create a temporary link element with Apple QuickLook attributes
        const quickLookLink = document.createElement('a');
        
        // Convert relative URI to absolute if needed
        const absoluteUri = modelUri.startsWith('http') ? modelUri : 
                           modelUri.startsWith('/') ? window.location.origin + modelUri :
                           window.location.origin + '/' + modelUri;
        
        quickLookLink.href = absoluteUri;
        quickLookLink.rel = 'ar';  // 🍎 Critical: This triggers Apple QuickLook AR
        
        // 🍎 REQUIRED: Apple demands a child img or picture element for rel="ar" links
        const placeholderImg = document.createElement('img');
        placeholderImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'; // 1x1 transparent GIF
        placeholderImg.alt = `AR Preview: ${poiName}`;
        placeholderImg.style.width = '1px';
        placeholderImg.style.height = '1px';
        placeholderImg.style.opacity = '0';
        
        quickLookLink.appendChild(placeholderImg);
        
        // Hide the link (it's just for triggering)
        quickLookLink.style.display = 'none';
        
        // Add to DOM temporarily
        document.body.appendChild(quickLookLink);
        
        //Logger.Instance.Info(`🍎 Created QuickLook link: ${absoluteUri}`, 'QuickLook');
        //Logger.Instance.Info('🍎 Triggering Apple QuickLook AR...', 'QuickLook');
        
        // Trigger the QuickLook AR by clicking the link
        quickLookLink.click();
        
        // Clean up after a short delay
        setTimeout(() => {
            document.body.removeChild(quickLookLink);
            //Logger.Instance.Info('🍎 QuickLook link cleaned up', 'QuickLook');
        }, 1000);
        
        // Show user feedback
        this.showQuickLookFeedback(poiName, absoluteUri);
    }

    /**
     * Show user feedback for QuickLook AR trigger
     */
    private showQuickLookFeedback(poiName: string, modelUri: string): void {
        // Create temporary feedback overlay
        const feedback = document.createElement('div');
        feedback.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: rgba(0, 0, 0, 0.8);
            color: white;
            padding: 20px;
            border-radius: 10px;
            z-index: 10000;
            text-align: center;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            max-width: 300px;
        `;
        
        feedback.innerHTML = `
            <div style="font-size: 24px; margin-bottom: 10px;">🍎📱</div>
            <div style="font-weight: bold; margin-bottom: 8px;">Apple QuickLook AR</div>
            <div style="margin-bottom: 8px;">"${poiName}"</div>
            <div style="font-size: 12px; opacity: 0.8;">
                ${this.isIOSSafari() ? 
                  'Tap "AR" in the preview to place in your space' : 
                  'This feature requires iOS Safari'}
            </div>
        `;
        
        document.body.appendChild(feedback);
        
        // Auto-remove after 3 seconds
        setTimeout(() => {
            if (feedback.parentNode) {
                document.body.removeChild(feedback);
            }
        }, 3000);
    }

    /**
     * Show AR error message to user
     */
    private showARError(title: string, message: string): void {
        const errorFeedback = document.createElement('div');
        errorFeedback.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: rgba(220, 38, 38, 0.9);
            color: white;
            padding: 20px;
            border-radius: 10px;
            z-index: 10000;
            text-align: center;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            max-width: 300px;
            border: 2px solid rgba(220, 38, 38, 1);
        `;
        
        errorFeedback.innerHTML = `
            <div style="font-size: 24px; margin-bottom: 10px;">⚠️</div>
            <div style="font-weight: bold; margin-bottom: 8px;">${title}</div>
            <div style="font-size: 14px;">${message}</div>
        `;
        
        document.body.appendChild(errorFeedback);
        
        // Auto-remove after 3 seconds
        setTimeout(() => {
            if (errorFeedback.parentNode) {
                document.body.removeChild(errorFeedback);
            }
        }, 3000);
        
        Logger.Instance.Warn(`⚠️ AR Error: ${title} - ${message}`, 'QuickLook');
    }

    /**
     * Configure mobile camera behavior via settings properties
     * @param distanceMultiplier Multiply desktop distance by this factor (default: 0.58 = 58% closer)
     * @param latitudeOffset Degrees to add to latitude for better viewing (default: +4°)
     * @param sensitivityMultiplier Touch sensitivity multiplier (default: 1.5x desktop)
     * @param enabled Enable/disable mobile optimization (default: true)
     */
    public configureMobileCamera(
        distanceMultiplier: number = 0.58,
        latitudeOffset: number = 4,
        sensitivityMultiplier: number = 1.5,
        enabled: boolean = true
    ): void {
        // Configure via AppSettingsData properties (integrates with UI)
        this.appSettings.enableMobileCameraOptimization.value = enabled;
        this.appSettings.mobileCameraDistanceMultiplier.value = distanceMultiplier;
        this.appSettings.mobileCameraLatitudeOffset.value = latitudeOffset;
        this.appSettings.mobileCameraSensitivityMultiplier.value = sensitivityMultiplier;
        
        //Logger.Instance.Info(`📱 Mobile camera configured via settings: enabled=${enabled}, distance=${distanceMultiplier}x, latitude=${latitudeOffset > 0 ? '+' : ''}${latitudeOffset}°, sensitivity=${sensitivityMultiplier}x`, 'MobileCamera');
        
        // Apply settings immediately if on mobile
        if (this.camera) {
            // The camera will automatically pick up the new settings on next update cycle
            //Logger.Instance.Info('📱 Mobile camera settings will be applied automatically', 'MobileCamera');
        } else {
            Logger.Instance.Warn('📱 Camera not initialized yet - settings will be applied when camera is created', 'MobileCamera');
        }
    }

    /**
     * Get AR status for UI
     */
    public getARStatus(): {
        isSupported: boolean;
        isActive: boolean;
        session: XRSession | null;
    } {
        return this.arManager.getStatus();
    }

    private setupFieldAwarenessPropertyHandlers(): void {
        if (!this.layoutData) return;

        // Field Awareness Enable/Disable
        this.layoutData.fieldAwarenessEnabled.on((enabled) => {
            //Logger.Instance.Info(`🌪️ Field Awareness property changed to: ${enabled}`, 'Field');
            if (enabled) {
                //Logger.Instance.Info('🌪️ Enabling Field Awareness from property change', 'Field');
                this.enableFieldAwareness();
            } else {
                //Logger.Instance.Info('🌪️ Disabling Field Awareness from property change', 'Field');
                this.disableFieldAwareness();
            }
        });

        // Field Layout Type Change
        this.layoutData.fieldLayoutType.on((layoutType) => {
            //Logger.Instance.Info(`🌪️ Field Layout Type changed to: ${layoutType}`, 'Field');
            if (this.layoutData.fieldAwarenessEnabled.value && layoutType) {
                this.setFieldLayout(layoutType as 'line' | 'grid' | 'circle' | 'spiral' | 'hex' | 'hex-spherical' | 'grid-spherical');
            }
        });

        // Field Parameter Updates
        const fieldParameterUpdater = () => {
            if (this.fieldContext && this.layoutData.fieldAwarenessEnabled.value) {
                // Convert string values to proper enum types
                const hoverDirectionValue = this.layoutData.fieldHoverDirection.value ?? 'transversal';
                const alignmentModeValue = this.layoutData.fieldAlignmentMode.value ?? 'aligned';
                const upVectorModeValue = this.layoutData.fieldUpVectorMode.value ?? 'world-up';
                const hoverDirectionOverrideValue = this.layoutData.fieldHoverDirectionOverride.value ?? 'none';
                
                const params = {
                    hoverAmplitude: this.layoutData.fieldHoverAmplitude.value ?? 1.5,
                    hoverFrequency: this.layoutData.fieldHoverFrequency.value ?? 0.1,
                    hoverPhaseShift: this.layoutData.fieldHoverPhaseShift.value ?? 0.0,
                    rotateAmplitude: this.layoutData.fieldRotateAmplitude.value ?? 0.5,
                    rotateFrequency: this.layoutData.fieldRotateFrequency.value ?? 0.8,
                    rotatePhaseShift: this.layoutData.fieldRotatePhaseShift.value ?? 1.57,
                    rotationMode: this.layoutData.fieldRotationMode.value === 'infinite' ? RotationMode.Infinite : RotationMode.Oscillating,
                    scaleAmplitude: this.layoutData.fieldScaleAmplitude.value ?? 0.2,
                    waveSpeed: this.layoutData.fieldWaveSpeed.value ?? 1.5,
                    windStrength: this.layoutData.fieldWindStrength.value ?? 0.3,
                    breathingIntensity: this.layoutData.fieldBreathingIntensity.value ?? 1.0,
                    organicVariation: this.layoutData.fieldOrganicVariation.value ?? 0.3,
                    hoverDirection: hoverDirectionValue === 'transversal' ? HoverDirection.Transversal : HoverDirection.Longitudinal,
                    alignmentMode: alignmentModeValue === 'aligned' ? AlignmentMode.Aligned : AlignmentMode.Perpendicular,
                    upVectorMode: upVectorModeValue === 'world-up' ? UpVectorMode.WorldUp : UpVectorMode.PathUp,
                    hoverDirectionOverride: hoverDirectionOverrideValue as 'none' | 'vertical' | 'horizontal',
                    fieldDirection: this.layoutData.fieldDirection.value ?? new Vector3(0, 0, 1),
                    cellSize: this.layoutData.fieldCellSize.value ?? 2,
                    cellSpacing: this.layoutData.fieldCellSpacing.value ?? 0,
                    customUpVector: this.layoutData.fieldCustomUpVector.value ?? new Vector3(0, 1, 0),
                    customAlignmentVector: this.layoutData.fieldCustomAlignmentVector.value ?? new Vector3(0, 0, 1)
                };
                
                this.fieldContext.updateParameters(params);
                //Logger.Instance.Debug('🌪️ Updated field parameters from UI settings', 'Field');
            }
        };

        // Connect all field parameter properties (including enum properties)
        [
            this.layoutData.fieldHoverAmplitude,
            this.layoutData.fieldHoverFrequency,
            this.layoutData.fieldHoverPhaseShift,
            this.layoutData.fieldRotateAmplitude,
            this.layoutData.fieldRotateFrequency,
            this.layoutData.fieldRotatePhaseShift,
            this.layoutData.fieldRotationMode,
            this.layoutData.fieldScaleAmplitude,
            this.layoutData.fieldWaveSpeed,
            this.layoutData.fieldWindStrength,
            this.layoutData.fieldBreathingIntensity,
            this.layoutData.fieldOrganicVariation,
            this.layoutData.fieldHoverDirection,
            this.layoutData.fieldAlignmentMode,
            this.layoutData.fieldUpVectorMode,
            this.layoutData.fieldHoverDirectionOverride,
            this.layoutData.fieldDirection,
            this.layoutData.fieldCellSize,
            this.layoutData.fieldCellSpacing,
            this.layoutData.fieldCustomUpVector,
            this.layoutData.fieldCustomAlignmentVector
        ].forEach(property => {
            property.on(() => fieldParameterUpdater());
        });

        // IMPORTANT: Initial sync of field parameters with UI settings at startup
        //Logger.Instance.Info('🌪️ Performing initial field parameter sync with UI settings', 'Field');
        fieldParameterUpdater(); // Call once to sync current UI values
        
        //Logger.Instance.Info('🌪️ Connected Field Awareness properties to live parameter updates', 'Field');
    }

    // ==================== MOUSE DRAG TRACKING ====================
    
    /**
     * Update mouse/touch drag tracking for click suppression after camera movements
     */
    private updateMouseDragTracking(): void {
        const inputManager = InputEventManager.Instance;
        
        // On mouse/touch down, record starting position and reset drag distance
        const isInputDown = inputManager.isMouseDown || inputManager.isTouchDown;
        const currentPosition = inputManager.isTouchDown ? inputManager.touchPosition : inputManager.mousePosition;
        const currentDelta = inputManager.isTouchDown ? inputManager.touchDelta : inputManager.mouseDelta;
        
        if (isInputDown && this._mouseDownPosition.x < 0) {
            this._mouseDownPosition.copy(currentPosition);
            this._totalMouseDragDistance = 0;
            //Logger.Instance.Debug(`🖱️👆 Input down recorded at (${this._mouseDownPosition.x}, ${this._mouseDownPosition.y})`, 'Input');
        }
        
        // While input is down, accumulate drag distance
        if (isInputDown && this._mouseDownPosition.x >= 0) {
            const deltaLength = Math.sqrt(
                currentDelta.x * currentDelta.x + 
                currentDelta.y * currentDelta.y
            );
            this._totalMouseDragDistance += deltaLength;
        }
        
        // On input up, reset tracking
        if (!isInputDown && this._mouseDownPosition.x >= 0) {
            //Logger.Instance.Debug(`🖱️👆 Input up: total drag distance was ${this._totalMouseDragDistance.toFixed(1)}px`, 'Input');
            this._mouseDownPosition.set(-1, -1); // Mark as invalid
            // Note: Don't reset _totalMouseDragDistance here, as click event comes after input up
        }
    }

    /**
     * Setup AR event handlers
     */
    private setupAREventHandlers(): void {
        // Listen for AR support check results
        this.eventManager.on('ar:support-checked', (event: any) => {
            // AR state managed by PresentationToolbar
        });
        
        // Listen for AR session start/end
        this.eventManager.on('ar:session-started', () => {
            this.showAROverlay();
        });
        
        this.eventManager.on('ar:session-ended', () => {
            this.hideAROverlay();
        });
        
        // Listen for AR errors
        this.eventManager.on('ar:error', (event: any) => {
            console.error('🥽 AR Error:', event.error);
            this.showARError('AR Error', event.error || 'Unknown AR error');
        });
        
        //Logger.Instance.Info('🥽 AR event handlers setup complete', 'AR');
    }

    /**
     * AR functionality is now integrated into PresentationToolbar
     */
    private createARButton(): void {
        // AR button is now part of the PresentationToolbar - no separate button needed
        //Logger.Instance.Info('🥽 AR functionality integrated into PresentationToolbar', 'AR');
    }

    /**
     * Update AR button state - now handled by PresentationToolbar
     */
    private updateARButton(): void {
        // AR functionality is now integrated into PresentationToolbar
        // The toolbar handles platform detection and state management
    }

    /**
     * Show AR overlay when AR is active
     */
    private showAROverlay(): void {
        if (this._arOverlay) return; // Already showing
        
        this._arOverlay = document.createElement('div');
        this._arOverlay.style.cssText = `
            position: fixed;
            bottom: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: rgba(0, 0, 0, 0.9);
            color: white;
            padding: 16px 24px;
            border-radius: 12px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 14px;
            text-align: center;
            z-index: 1001;
            backdrop-filter: blur(10px);
            border: 2px solid rgba(255, 255, 255, 0.2);
        `;
        
        this._arOverlay.innerHTML = `
            <div style="margin-bottom: 8px; font-weight: bold; color: #4CAF50;">🥽 AR Mode Active</div>
            <div style="margin-bottom: 4px;">📱 Using device camera - move to explore</div>
            <div style="margin-bottom: 4px;">🎥 POI Camera temporarily disabled</div>
            <div style="font-size: 12px; opacity: 0.8;">Press V or click AR button to exit</div>
        `;
        
        document.body.appendChild(this._arOverlay);
        
        //Logger.Instance.Info('🥽 AR overlay shown', 'AR');
    }

    /**
     * Hide AR overlay
     */
    private hideAROverlay(): void {
        if (this._arOverlay) {
            document.body.removeChild(this._arOverlay);
            this._arOverlay = null;
            //Logger.Instance.Info('🥽 AR overlay hidden', 'AR');
        }
    }


    /**
     * Remove AR button from UI
     */
    private removeARButton(): void {
        // AR button is now integrated into PresentationToolbar - no separate button to remove
        //Logger.Instance.Info('🥽 AR functionality integrated into toolbar', 'AR');
    }

    // ==================== SPECTATOR CAMERA SYSTEM ====================
    
    /**
     * Toggle between POI camera and spectator (fly) camera
     */
    public toggleSpectatorMode(): void {
        if (!this.flyCamera || !this.camera) {
            Logger.Instance.Warn('⚠️ Camera system not initialized', 'Camera');
            return;
        }

        this.isSpectatorMode = !this.isSpectatorMode;

        if (this.isSpectatorMode) {
            // Switch to spectator mode
            //Logger.Instance.Info('🎮 Entering Spectator Mode - use WASD + mouse to fly around', 'Camera');
            //Logger.Instance.Info('🎮 Press Ctrl+K again to return to POI camera mode', 'Camera');
            
            // Copy current camera position to fly camera
            const currentPosition = this.camera.camera.position;
            const currentRotation = this.camera.camera.quaternion;
            
            //Logger.Instance.Debug(`📍 Current camera position: ${currentPosition.x.toFixed(2)}, ${currentPosition.y.toFixed(2)}, ${currentPosition.z.toFixed(2)}`, 'SpectatorCamera');
            //Logger.Instance.Debug(`🔄 Current camera rotation: ${currentRotation.x.toFixed(2)}, ${currentRotation.y.toFixed(2)}, ${currentRotation.z.toFixed(2)}, ${currentRotation.w.toFixed(2)}`, 'SpectatorCamera');
            
            this.flyCamera.transform.position.setImmediate(
                new Vector3(currentPosition.x, currentPosition.y, currentPosition.z)
            );
            
            // Use the new method to properly initialize pitch/yaw from quaternion
            const quaternion = new Quaternion(currentRotation.x, currentRotation.y, currentRotation.z, currentRotation.w);
            this.flyCamera.setRotationFromQuaternion(quaternion);
            
            // Activate fly camera
            this.flyCamera.setActive(true);
            //Logger.Instance.Info('🚁 FlyCamera activated and ready for input', 'SpectatorCamera');
            
        } else {
            // Switch back to POI mode
            //Logger.Instance.Info('🎯 Returning to POI Camera Mode', 'Camera');
            
            // Deactivate fly camera
            this.flyCamera.setActive(false);
            
            // Set target to current active POI if available
            const activePOI = this.pois.find(poi => poi.isActive);
            if (activePOI) {
                this.camera.setTarget(activePOI);
            }
        }
    }

    /**
     * 🎯 Explicitly trigger texture loading for all POIs AFTER MaterialLoadingManager registration
     */
    private triggerTextureLoadingForAllPOIs(): void {
        //Logger.Instance.Info('🚀 Triggering texture loading for all POIs after registration', 'MaterialLoading');
        //console.log(`🔍 [DEBUG] triggerTextureLoadingForAllPOIs: Found ${this.pois.length} POIs`);
        
        for (const poi of this.pois) {
            //console.log(`🔍 [DEBUG] Processing POI: ${poi.name}`);
            //console.log(`  📦 Shape: ${poi.shape?.constructor.name || 'undefined'}`);
            //console.log(`  📁 URIs: ${JSON.stringify(poi.poidata.uri.value || [])}`);
            const uris = poi.poidata.uri.value || []; // Now an array
            //Logger.Instance.Debug(`🔍 Checking POI: ${poi.name}, uris: ${uris}`, 'MaterialLoading');
            
            // Process all URIs in the array (typically front + optional back texture)
            for (const singleUri of uris) {
                if (singleUri && singleUri.trim() !== '') {
                    const isVideo = ['.mp4', '.webm', '.ogv'].some(ext => singleUri.toLowerCase().endsWith(ext));
                    const isGLTF = ['.glb', '.gltf'].some(ext => singleUri.toLowerCase().endsWith(ext));
                    const isUSDZ = singleUri.toLowerCase().endsWith('.usdz');
                    const isTexture = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'].some(ext => singleUri.toLowerCase().endsWith(ext));
                
                // Only trigger texture loading for actual texture/video materials, not GLB or USDZ files
                //console.log(`🔍 [DEBUG] URI '${singleUri}': isTexture=${isTexture}, isVideo=${isVideo}, isUSDZ=${isUSDZ}`);
                if ((isTexture || isVideo) && !isUSDZ) {
                    //console.log(`🎯 [DEBUG] Processing texture/video URI: ${singleUri}`);
                    //Logger.Instance.Debug(`🔍 POI: ${poi.name}, isVideo: ${isVideo}`, 'MaterialLoading');
                    
                    // 🎯 NEW: Trigger texture loading for ALL materials (primary + secondary)
                    const primaryMaterial = poi.shape?.primaryMaterial;
                    const secondaryMaterial = poi.shape?.secondaryMaterial;
                    
                    // Check primary material
                    const hasPrimaryRequestMethod = typeof (primaryMaterial as any)?.requestTextureLoad;
                    if (primaryMaterial && hasPrimaryRequestMethod === 'function') {
                        //Logger.Instance.Info(`🖼️ Triggering texture load for POI: ${poi.name} (primary material, ${isVideo ? 'video' : 'image'})`, 'MaterialLoading');
                        (primaryMaterial as any).requestTextureLoad();
                    }
                    
                    // 🎯 NEW: Check secondary material (back texture!)
                    const hasSecondaryRequestMethod = typeof (secondaryMaterial as any)?.requestTextureLoad;
                    if (secondaryMaterial && hasSecondaryRequestMethod === 'function') {
                        //Logger.Instance.Info(`🖼️ Triggering texture load for POI: ${poi.name} (secondary material, ${isVideo ? 'video' : 'image'})`, 'MaterialLoading');
                        (secondaryMaterial as any).requestTextureLoad();
                    }
                    
                    // 🎯 CRITICAL: Access MaterialSet directly from Mesh3D to get back material
                    const mesh3D = poi.shape;
                    //console.log(`🔍 [DEBUG] Checking MaterialSet: mesh3D=${!!mesh3D}, _materialSet=${!!(mesh3D as any)?._materialSet}`);
                    if (mesh3D && (mesh3D as any)._materialSet) {
                        const materialSet = (mesh3D as any)._materialSet;
                        //console.log(`🔍 [DEBUG] MaterialSet found: front=${!!materialSet.front}, back=${!!materialSet.back}, sides=${!!materialSet.sides}`);
                        
                        if (materialSet.back) {
                            const hasRequestMethod = typeof (materialSet.back as any)?.requestTextureLoad;
                            //console.log(`🔍 [DEBUG] Back material: ${materialSet.back.constructor.name}, hasRequestTextureLoad=${hasRequestMethod}`);
                            
                            if (hasRequestMethod === 'function') {
                                //Logger.Instance.Info(`🖼️ Triggering texture load for POI: ${poi.name} (back material from MaterialSet, ${isVideo ? 'video' : 'image'})`, 'MaterialLoading');
                                (materialSet.back as any).requestTextureLoad();
                            }
                        }
                    }
                    
                    if (!primaryMaterial && !secondaryMaterial) {
                        Logger.Instance.Warn(`⚠️ Cannot trigger texture load for POI: ${poi.name} - no materials found`, 'MaterialLoading');
                    }
                } else if (isGLTF) {
                    //Logger.Instance.Info(`🏗️ GLB/GLTF file detected for POI: ${poi.name} - loading handled by GLTFShape`, 'MaterialLoading');
                }
            } // End of URI loop
        } // End of POI loop
    }
}
}
